// ═══════════════════════════════════════════════════════════════
// ExpertiseBasesPanel — Vue des bases d'expertise V2
// Phase B1 (logique) + Phase B2 (redesign premium bleu nuit violet)
// ═══════════════════════════════════════════════════════════════
//
// UX :
//   - Liste des bases accessibles (cards premium hover violet)
//   - Création d'une base depuis un template actif (modal stylée)
//   - Sélection d'une base → vue détail header premium + stats +
//     zone upload drop-style + liste documents en cards
//   - Suppression d'un document (soft-delete) via confirmModal
//
// Accent couleur : violet #8b5cf6 (cohérent avec le pill "📚 N bases"
// du header ProjectDetail et la section 6 ProjectSettings).
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
    Database, Plus, ArrowLeft, Upload, Trash2, FileText, Loader, CheckCircle2,
    AlertCircle, BookOpen, FlaskConical, Sparkles, BookMarked, File, FileCode,
    Layers, Activity, Calendar, HardDrive, Copy, Download, Check, Hammer, Info, RefreshCw, ChevronDown, Pencil, Users, RotateCcw,
    Award, GitBranch, Archive, ArchiveRestore, ServerCrash, Gauge, Tag, FolderOpen, Lock,
    Search, Wand2, LayoutGrid, List, ArrowDownUp, X
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import SummarizerModelPicker from '../bench/SummarizerModelPicker';
import { MODEL_PROFILES, PICKER_ORDER } from '../bench/modelProfiles';
import BenchHelpModal from '../bench/BenchHelpModal';
import ScoreCard from './ScoreCard';
import MetaSummaryCard from './MetaSummaryCard';

const API_BASE = '/api/expertise-bases';
const ACCENT = '#8b5cf6';
const ACCENT_DARK = '#7c3aed';
const ACCENT_GLOW = 'rgba(139, 92, 246, 0.25)';
const ACCENT_TINT = 'rgba(139, 92, 246, 0.08)';
const ACCENT_BORDER = 'rgba(139, 92, 246, 0.3)';

const getToken = () => localStorage.getItem('oracle_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// Style d'un chip de filtre (actif / inactif)
const filterChip = (active) => ({
    padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
    border: `1px solid ${active ? ACCENT : 'var(--card-border)'}`,
    background: active ? 'rgba(139,92,246,0.12)' : 'var(--card-bg)',
    color: active ? ACCENT : 'var(--text-secondary)',
    fontWeight: active ? 600 : 500, whiteSpace: 'nowrap', transition: 'all .15s',
    display: 'inline-flex', alignItems: 'center', gap: 5,
});
// Taxonomie suggérée (libre d'en créer d'autres)
const SUGGESTED_CATEGORIES = ['Doctrine', 'Pédagogie', 'Production', 'Benchmark/Test', 'Documentation', 'Modèles', 'Autre'];

// Icônes par slug de template
const TEMPLATE_ICONS = {
    'corpus-methodologique-long': BookOpen,
    'corpus-scientifique': FlaskConical,
    'base-conceptuelle-structurante': Sparkles,
    'base-thematique-specialisee': Layers,
    'base-libre': BookMarked,
};

// Guidage utilisateur — descriptions claires + exemples concrets
const TEMPLATE_HINTS = {
    'base-libre': {
        description: 'Tu ne sais pas trop ? Choisis celui-ci. Le pipeline détecte automatiquement le format de ton document et applique le traitement le plus adapté.',
        examples: 'notes, rapports, présentations, documents mixtes',
        pages: 'tout format',
    },
    'base-thematique-specialisee': {
        description: 'Pour regrouper plusieurs documents courts autour d\'un même sujet. Chaque document est analysé finement par sections.',
        examples: 'articles de blog, fiches métier, billets d\'analyse, notes thématiques',
        pages: '1–30 pages',
    },
    'corpus-methodologique-long': {
        description: 'Pour les gros documents structurés avec chapitres. Découpage hiérarchique intelligent, synthèse en 3 niveaux.',
        examples: 'livres, manuels, méthodologies, thèses, rapports 100+ pages',
        pages: '50+ pages',
    },
    'corpus-scientifique': {
        description: 'Pour les publications académiques avec références, formules et structure IMRaD. Extraction DOI et bibliographie.',
        examples: 'articles de recherche, papers arXiv, publications peer-reviewed',
        pages: '10–60 pages',
    },
};

// Icône selon extension de fichier
const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return FileText;
    if (['doc', 'docx', 'odt'].includes(ext)) return FileText;
    if (['txt', 'md', 'markdown'].includes(ext)) return File;
    if (['json', 'csv', 'xml'].includes(ext)) return FileCode;
    return FileText;
};

const formatBytes = (bytes) => {
    if (!bytes || bytes < 1024) return `${bytes || 0} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
};

// ═══════════════════════════════════════════════════════════════
// SUMMARY PREVIEW PANEL — Right-side panel showing document summary
// with level1 expandable sections
// ═══════════════════════════════════════════════════════════════
function SummaryPreviewPanel({ doc, base, onRefresh }) {
    const [copied, setCopied] = useState(false);
    const [refining, setRefining] = useState(false);
    const [showRefineDropdown, setShowRefineDropdown] = useState(false);
    const [showSections, setShowSections] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [fullDoc, setFullDoc] = useState(null);
    const [loadingSections, setLoadingSections] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [editText, setEditText] = useState('');
    const [savingSection, setSavingSection] = useState(false);
    const refineRef = useRef(null);

    // Role global user (décodé JWT) — pour savoir si admin/fondateur bypass les bases système.
    const currentUserRole = useMemo(() => {
        try {
            const token = localStorage.getItem('oracle_token');
            if (!token) return null;
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.role || null;
        } catch { return null; }
    }, []);
    const isGlobalAdmin = currentUserRole === 'admin_general' || currentUserRole === 'oracle_fondateur';
    // Éditer les sections (chunks) : autorisé pour owner/editor, mais bloqué sur base système sauf admin/fondateur.
    const canEditSections = (base?.user_role === 'owner' || base?.user_role === 'editor') && (!base?.is_system || isGlobalAdmin);
    const editBlockedReason = base?.is_system && !isGlobalAdmin
        ? 'Base système — édition réservée aux administrateurs'
        : (base?.user_role === 'reader' ? 'Accès en lecture seule' : '');

    // Charger le document complet (avec summary_tree level1) à la demande
    useEffect(() => {
        if (!showSections || !doc?.id || !base?.id) return;
        if (fullDoc?.id === doc.id) return; // déjà chargé
        setLoadingSections(true);
        fetch(`${API_BASE}/${base.id}/documents/${doc.id}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(data => { setFullDoc(data); setLoadingSections(false); })
            .catch(() => setLoadingSections(false));
    }, [showSections, doc?.id, base?.id, fullDoc?.id]);

    // Reset quand on change de doc
    useEffect(() => {
        setShowSections(false);
        setExpandedSections({});
        setFullDoc(null);
        setEditingSection(null);
        setEditText('');
    }, [doc?.id]);

    // Hook must be above ALL early returns (Rules of Hooks)
    useEffect(() => {
        if (!showRefineDropdown) return;
        const handler = (e) => {
            if (refineRef.current && !refineRef.current.contains(e.target)) {
                setShowRefineDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showRefineDropdown]);

    // Level1 sections data
    const level1Results = fullDoc?.summary_tree?.level1?.results?.filter(r => r.success && r.summary) || [];
    const toggleSection = (idx) => setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));

    const startEditing = (idx, currentSummary) => {
        setEditingSection(idx);
        setEditText(currentSummary || '');
    };
    const cancelEditing = () => { setEditingSection(null); setEditText(''); };
    // Map filtered index → real index in summary_tree.level1.results[]
    const allResults = fullDoc?.summary_tree?.level1?.results || [];
    const filteredToRealIdx = allResults.reduce((acc, r, i) => { if (r.success && r.summary) acc.push(i); return acc; }, []);

    const saveSection = async (filteredIdx) => {
        if (!editText.trim() || savingSection) return;
        const realIdx = filteredToRealIdx[filteredIdx];
        if (realIdx === undefined) return;
        setSavingSection(true);
        try {
            const res = await fetch(`${API_BASE}/${base.id}/documents/${doc.id}/sections/${realIdx}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ summary: editText }),
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Erreur'); }
            const data = await res.json();
            // Update local state
            const updatedTree = { ...fullDoc.summary_tree };
            updatedTree.level1.results[realIdx] = { ...updatedTree.level1.results[realIdx], ...data.section };
            setFullDoc({ ...fullDoc, summary_tree: updatedTree });
            setEditingSection(null);
            setEditText('');
            toast.success('Section mise à jour');
        } catch (err) {
            toast.error(`Erreur : ${err.message}`);
        } finally {
            setSavingSection(false);
        }
    };

    if (!doc) {
        return (
            <div style={{
                width: '100%',
                background: 'var(--card-bg)', border: `1px solid var(--card-border)`,
                borderRadius: 14, padding: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)', fontSize: 13, textAlign: 'center',
                minHeight: 300,
            }}>
                Sélectionne un document pour voir sa synthèse
            </div>
        );
    }

    // Check if summary exists — level2_summary est un champ flat retourné par l'API
    const summary = doc.level2_summary || doc.summary_tree?.level2?.summary || null;
    // Résolution du modèle : doc level2_model > base override > template default
    const summaryModel = doc.level2_model
        || doc.summary_tree?.level2?.model
        || base?.summarizer_override?.level1_llm
        || base?.template?.pipeline_config?.summary_tree?.level1_llm
        || 'Modèle par défaut';

    if (!summary) {
        return (
            <div style={{
                width: '100%',
                background: 'var(--card-bg)', border: `1px solid var(--card-border)`,
                borderRadius: 14, padding: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)', fontSize: 13, textAlign: 'center',
                minHeight: 300,
            }}>
                Synthèse non disponible pour ce document
            </div>
        );
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(summary);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Erreur copie:', err);
        }
    };

    const handleDownloadMarkdown = () => {
        const blob = new Blob([summary], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.original_name || 'resume'}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadText = () => {
        const blob = new Blob([summary], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.original_name || 'resume'}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleRefine = async (modelKey) => {
        setShowRefineDropdown(false);
        setRefining(true);
        try {
            const res = await fetch(`${API_BASE}/${base.id}/documents/${doc.id}/refine`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ model: modelKey }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Erreur lors du raffinement');
            }
            const result = await res.json();
            toast.success(`Synthèse raffinée avec ${MODEL_PROFILES[modelKey]?.label || modelKey} — $${result.costUsd?.toFixed(4) || '?'}`);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setRefining(false);
        }
    };

    const docDisplayName = doc.original_name || 'Document';
    const truncatedName = docDisplayName.length > 30
        ? docDisplayName.substring(0, 27) + '...'
        : docDisplayName;

    // Boutons actions réutilisables (en haut + en bas)
    const ActionButtons = () => (
        <div style={{ display: 'flex', gap: 6 }}>
            <button
                onClick={handleCopy}
                title="Copier la synthèse"
                style={{
                    padding: '5px 10px', fontSize: 10, fontWeight: 600,
                    background: copied ? '#22c55e' : 'var(--input-bg)',
                    color: copied ? 'white' : 'var(--text-secondary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'center', gap: 4,
                }}
            >
                {copied ? <Check size={10} /> : <Copy size={10} />}
                {copied ? 'Copié' : 'Copier'}
            </button>
            <button
                onClick={handleDownloadMarkdown}
                title="Télécharger .md"
                style={{
                    padding: '5px 10px', fontSize: 10, fontWeight: 600,
                    background: 'var(--input-bg)', color: 'var(--text-secondary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
            >
                <Download size={10} /> .md
            </button>
            <button
                onClick={handleDownloadText}
                title="Télécharger .txt"
                style={{
                    padding: '5px 10px', fontSize: 10, fontWeight: 600,
                    background: 'var(--input-bg)', color: 'var(--text-secondary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
            >
                <Download size={10} /> .txt
            </button>
            {/* Raffinement — bouton + dropdown (owner only, la route backend rejette les autres) */}
            {base?.user_role === 'owner' && (
            <div ref={refineRef} style={{ position: 'relative' }}>
                <button
                    onClick={() => setShowRefineDropdown(!showRefineDropdown)}
                    disabled={refining}
                    title="Raffiner la synthèse avec un autre modèle"
                    style={{
                        padding: '5px 10px', fontSize: 10, fontWeight: 600,
                        background: refining ? ACCENT : 'var(--input-bg)',
                        color: refining ? 'white' : ACCENT,
                        border: `1px solid ${refining ? ACCENT : 'rgba(139,92,246,0.3)'}`,
                        borderRadius: 6, cursor: refining ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        transition: 'all 0.2s ease',
                    }}
                >
                    {refining ? <Loader size={10} className="expertise-spin" /> : <RefreshCw size={10} />}
                    {refining ? 'Raffinement…' : 'Raffiner'}
                </button>
                {showRefineDropdown && !refining && (
                    <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4,
                        width: 220, maxHeight: 300, overflowY: 'auto',
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                        borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                        zIndex: 100, padding: '4px 0',
                    }}>
                        <div style={{
                            padding: '6px 12px', fontSize: 10, fontWeight: 600,
                            color: 'var(--text-muted)', textTransform: 'uppercase',
                            borderBottom: '1px solid var(--card-border)',
                        }}>
                            Choisir un modèle
                        </div>
                        {PICKER_ORDER.map(key => {
                            const profile = MODEL_PROFILES[key];
                            if (!profile) return null;
                            return (
                                <button
                                    key={key}
                                    onClick={() => handleRefine(key)}
                                    style={{
                                        width: '100%', padding: '8px 12px',
                                        background: 'transparent', border: 'none',
                                        cursor: 'pointer', textAlign: 'left',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        fontSize: 12, color: 'var(--text)',
                                        transition: 'background 0.1s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <span style={{ fontWeight: 500 }}>{profile.label}</span>
                                    <span style={{
                                        fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                        background: `${profile.tier_color}20`, color: profile.tier_color,
                                        fontWeight: 600,
                                    }}>
                                        {profile.bench.score}/25
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            )}
        </div>
    );

    return (
        <div style={{
            width: '100%',
            background: 'var(--card-bg)', border: `1px solid var(--card-border)`,
            borderLeft: '3px solid #1e40af',
            borderRadius: 14, padding: 20,
            display: 'flex', flexDirection: 'column', gap: 12,
            overflowY: 'auto', maxHeight: 'calc(100vh - 200px)',
        }}>
            {/* Header + boutons en haut */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
                    }}>
                        Synthèse
                    </div>
                    <div style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={docDisplayName}>
                        {truncatedName}
                    </div>
                </div>
                <ActionButtons />
            </div>

            {/* Model info chip */}
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '3px 8px', borderRadius: 12,
                background: 'rgba(30, 64, 175, 0.12)', border: '1px solid rgba(30, 64, 175, 0.3)',
                fontSize: 10, fontWeight: 500, color: '#60a5fa',
                width: 'fit-content',
            }}>
                <Sparkles size={10} style={{ color: '#3b82f6' }} />
                {summaryModel}
            </div>

            {/* Summary content — Markdown rendered */}
            <div style={{
                fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)',
                padding: '8px 0',
            }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                    {summary}
                </ReactMarkdown>
            </div>

            {/* ── Level1 sections expandable ── */}
            <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 10 }}>
                <button
                    onClick={() => setShowSections(!showSections)}
                    style={{
                        width: '100%', padding: '8px 12px',
                        background: showSections ? 'rgba(139, 92, 246, 0.06)' : 'var(--input-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 8, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)',
                        transition: 'all 0.15s ease',
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Layers size={13} style={{ color: ACCENT }} />
                        Synthèses par section
                        {(() => {
                            const editedCount = fullDoc?.summary_tree?.level1?.results?.filter(s => s.edited_at)?.length || 0;
                            return editedCount > 0 ? (
                                <span style={{
                                    fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                                    background: 'rgba(217, 119, 6, 0.1)', color: '#d97706',
                                }}>
                                    {editedCount} modif.
                                </span>
                            ) : null;
                        })()}
                    </span>
                    <ChevronDown
                        size={14}
                        style={{
                            color: 'var(--text-muted)',
                            transform: showSections ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </button>

                {showSections && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {loadingSections ? (
                            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                                <Loader size={14} className="expertise-spin" style={{ display: 'inline-block', marginRight: 6 }} />
                                Chargement des sections…
                            </div>
                        ) : level1Results.length === 0 ? (
                            <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                                Aucune section disponible
                            </div>
                        ) : (
                            level1Results.map((section, idx) => {
                                const isExpanded = expandedSections[idx];
                                const isEditing = editingSection === idx;
                                const cleanTitle = (section.title || `Section ${idx + 1}`).replace(/\*\*/g, '');
                                const isEdited = !!section.edited_at;
                                return (
                                    <div key={idx} style={{
                                        border: '1px solid var(--card-border)',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        background: isExpanded ? 'rgba(139, 92, 246, 0.03)' : 'transparent',
                                        transition: 'background 0.15s ease',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <button
                                                onClick={() => toggleSection(idx)}
                                                style={{
                                                    flex: 1, padding: '8px 12px',
                                                    background: 'transparent', border: 'none',
                                                    cursor: 'pointer', textAlign: 'left',
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                    fontSize: 11.5, color: 'var(--text)',
                                                    fontWeight: isExpanded ? 600 : 500,
                                                }}
                                            >
                                                <ChevronDown
                                                    size={12}
                                                    style={{
                                                        color: 'var(--text-muted)', flexShrink: 0,
                                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.15s ease',
                                                    }}
                                                />
                                                <span style={{
                                                    flex: 1, overflow: 'hidden',
                                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {cleanTitle}
                                                </span>
                                                {isEdited && (
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 600,
                                                        color: '#d97706', background: 'rgba(217, 119, 6, 0.1)',
                                                        padding: '1px 6px', borderRadius: 4,
                                                        display: 'flex', alignItems: 'center', gap: 3,
                                                        flexShrink: 0,
                                                    }}>
                                                        <Pencil size={8} /> Modifié
                                                    </span>
                                                )}
                                                <span style={{
                                                    fontSize: 9, color: 'var(--text-muted)', flexShrink: 0,
                                                }}>
                                                    {section.summary?.length || 0} c.
                                                </span>
                                            </button>
                                            {isExpanded && !isEditing && (
                                                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    {isEdited && section.original_summary && canEditSections && (
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                const ok = await confirmModal('Restaurer le texte original généré par le modèle ?', { confirmText: 'Restaurer', danger: false });
                                                                if (!ok) return;
                                                                setEditText(section.original_summary);
                                                                setEditingSection(idx);
                                                                // Auto-save restauration
                                                                const realIdx = filteredToRealIdx[idx];
                                                                if (realIdx === undefined) return;
                                                                setSavingSection(true);
                                                                try {
                                                                    const res2 = await fetch(`${API_BASE}/${base.id}/documents/${doc.id}/sections/${realIdx}`, {
                                                                        method: 'PUT',
                                                                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({ summary: section.original_summary }),
                                                                    });
                                                                    if (!res2.ok) throw new Error('Erreur');
                                                                    const data = await res2.json();
                                                                    const updatedTree = { ...fullDoc.summary_tree };
                                                                    updatedTree.level1.results[realIdx] = { ...updatedTree.level1.results[realIdx], ...data.section };
                                                                    setFullDoc({ ...fullDoc, summary_tree: updatedTree });
                                                                    setEditingSection(null);
                                                                    setEditText('');
                                                                    toast.success('Section restaurée');
                                                                } catch { toast.error('Erreur de restauration'); }
                                                                finally { setSavingSection(false); }
                                                            }}
                                                            title="Restaurer l'original"
                                                            style={{
                                                                background: 'transparent', border: 'none',
                                                                cursor: 'pointer', padding: '6px 8px',
                                                                color: 'var(--text-muted)', flexShrink: 0,
                                                                transition: 'color 0.15s',
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.color = '#d97706'}
                                                            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                                                        >
                                                            <RotateCcw size={12} />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); if (canEditSections) startEditing(idx, section.summary); }}
                                                        disabled={!canEditSections}
                                                        title={canEditSections ? 'Éditer cette section' : editBlockedReason}
                                                        style={{
                                                            background: 'transparent', border: 'none',
                                                            cursor: canEditSections ? 'pointer' : 'not-allowed',
                                                            padding: '6px 10px',
                                                            color: canEditSections ? 'var(--text-muted)' : 'var(--text-muted)',
                                                            opacity: canEditSections ? 1 : 0.35,
                                                            flexShrink: 0,
                                                            transition: 'color 0.15s',
                                                        }}
                                                        onMouseEnter={e => { if (canEditSections) e.currentTarget.style.color = ACCENT; }}
                                                        onMouseLeave={e => { if (canEditSections) e.currentTarget.style.color = 'var(--text-muted)'; }}
                                                    >
                                                        <Pencil size={13} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {isExpanded && (
                                            <div style={{
                                                padding: '6px 12px 12px 32px',
                                                fontSize: 12, lineHeight: 1.65,
                                                color: 'var(--text-secondary)',
                                                borderTop: '1px solid var(--card-border)',
                                            }}>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        <textarea
                                                            value={editText}
                                                            onChange={e => setEditText(e.target.value)}
                                                            style={{
                                                                width: '100%', minHeight: 150,
                                                                padding: 10, fontSize: 12,
                                                                fontFamily: 'monospace', lineHeight: 1.6,
                                                                background: 'var(--input-bg)',
                                                                border: `1px solid ${ACCENT_BORDER}`,
                                                                borderRadius: 8, color: 'var(--text)',
                                                                resize: 'vertical', outline: 'none',
                                                            }}
                                                            onFocus={e => e.target.style.borderColor = ACCENT}
                                                            onBlur={e => e.target.style.borderColor = ACCENT_BORDER}
                                                            autoFocus
                                                        />
                                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                            <button
                                                                onClick={cancelEditing}
                                                                disabled={savingSection}
                                                                style={{
                                                                    padding: '6px 14px', fontSize: 11.5,
                                                                    background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                                                                    borderRadius: 6, cursor: 'pointer',
                                                                    color: 'var(--text-secondary)',
                                                                }}
                                                            >
                                                                Annuler
                                                            </button>
                                                            <button
                                                                onClick={() => saveSection(idx)}
                                                                disabled={savingSection || !editText.trim()}
                                                                style={{
                                                                    padding: '6px 14px', fontSize: 11.5,
                                                                    background: savingSection ? 'var(--text-muted)' : '#059669',
                                                                    border: 'none', borderRadius: 6,
                                                                    cursor: savingSection ? 'wait' : 'pointer',
                                                                    color: '#fff', fontWeight: 600,
                                                                }}
                                                            >
                                                                {savingSection ? 'Sauvegarde…' : 'Sauvegarder'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                                                        {section.summary}
                                                    </ReactMarkdown>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            {/* Footer — source bench */}
            <div style={{
                fontSize: 9, color: 'var(--text-muted)', paddingTop: 8,
                borderTop: '1px solid var(--card-border)', opacity: 0.7,
            }}>
                Source : ITRIIA BENCH 2026-04-12
            </div>
        </div>
    );
}

function ExpertiseBasesPanel({ user, initialBaseId, initialView, onConsumeInitial }) {
    // ─── État ─────────────────────────────────────────────────────
    const [bases, setBases] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedBase, setSelectedBase] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [previewDocId, setPreviewDocId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', slug: '', description: '', template_id: null, summarizer_model: '', canevas_prompt: '', category: '' });
    const [creating, setCreating] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [savingSummarizer, setSavingSummarizer] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [scoreModalBase, setScoreModalBase] = useState(null);
    const fileInputRef = useRef(null);

    // ─── Admin : bases archivées + métriques ressources ──────────
    const isAdmin = user?.role === 'admin_general';
    const [archivedBases, setArchivedBases] = useState([]);
    const [resourceMetrics, setResourceMetrics] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [unarchiving, setUnarchiving] = useState(null); // baseId en cours de restauration
    const [unarchiveProgress, setUnarchiveProgress] = useState(null);

    // ─── Édition inline titre + description ──────────────────────
    const [editingField, setEditingField] = useState(null); // 'name' | 'description' | null
    const [editValue, setEditValue] = useState('');
    const [savingField, setSavingField] = useState(false);

    // ─── Catégories ──────────────────────────────────────────────
    const COLLAPSE_LS = `itriia.expertise.collapsed.${user?.id || 'anon'}`;
    const [collapsedCategories, setCollapsedCategories] = useState(() => {
        try { return JSON.parse(localStorage.getItem(COLLAPSE_LS)) || {}; } catch { return {}; }
    });
    const [categoryAssigning, setCategoryAssigning] = useState(null); // baseId en cours d'assignation
    const [showCategorizeModal, setShowCategorizeModal] = useState(false);

    // ─── Recherche / filtres / vue / tri (affichage scannable) ────
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // all | system | models | mine | tpl:<slug>
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('itriia.expertise.viewMode') || 'grid'); // grid | list
    const [sortBy, setSortBy] = useState(() => localStorage.getItem('itriia.expertise.sortBy') || 'recent'); // recent | name | docs | score
    useEffect(() => { try { localStorage.setItem('itriia.expertise.viewMode', viewMode); } catch {} }, [viewMode]);
    useEffect(() => { try { localStorage.setItem('itriia.expertise.sortBy', sortBy); } catch {} }, [sortBy]);

    // ─── Chargement initial ───────────────────────────────────────
    const loadBases = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(API_BASE, { headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setBases(await res.json());
        } catch (err) {
            toast.error(`Chargement des bases : ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/templates`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setTemplates(await res.json());
        } catch (err) {
            toast.error(`Chargement du catalogue : ${err.message}`);
        }
    }, []);

    const loadDocuments = useCallback(async (baseId) => {
        try {
            const res = await fetch(`${API_BASE}/${baseId}/documents`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setDocuments(await res.json());
        } catch (err) {
            toast.error(`Chargement des documents : ${err.message}`);
        }
    }, []);

    useEffect(() => {
        loadBases();
        loadTemplates();
    }, [loadBases, loadTemplates]);

    useEffect(() => {
        if (selectedBase) {
            loadDocuments(selectedBase.id);
            setPreviewDocId(null); // Reset preview when changing base
        }
    }, [selectedBase, loadDocuments]);

    // Auto-select first done document for preview
    useEffect(() => {
        if (!previewDocId && documents.length > 0) {
            const firstDone = documents.find(d => d.processing_status === 'done' || d.processing_status === 'indexed');
            if (firstDone) {
                setPreviewDocId(firstDone.id);
            }
        }
    }, [documents, previewDocId]);

    // ─── Admin : chargement archivées + métriques ─────────────────
    const loadArchivedBases = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/admin/archived`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setArchivedBases(await res.json());
        } catch (err) {
            toast.error(`Bases archivées : ${err.message}`);
        }
    }, []);

    const loadResourceMetrics = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await fetch(`${API_BASE}/admin/resources`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setResourceMetrics(await res.json());
        } catch { /* silent */ }
    }, [isAdmin]);

    // Charger les archives au montage (pour afficher le count sur le bouton raccourci Archives)
    useEffect(() => {
        if (isAdmin) loadArchivedBases();
    }, [isAdmin, loadArchivedBases]);

    // S45 P1 — sélection automatique depuis la sidebar (initialBaseId / initialView)
    useEffect(() => {
        if (!bases || bases.length === 0) return;
        let consumed = false;
        if (initialBaseId) {
            const match = bases.find(b => b.id === initialBaseId);
            if (match) {
                setSelectedBase(match);
                consumed = true;
            }
        }
        if (initialView === 'archives' && isAdmin) {
            setShowArchived(true);
            consumed = true;
        }
        if (consumed) onConsumeInitial?.();
    }, [bases, initialBaseId, initialView, isAdmin, onConsumeInitial]);

    useEffect(() => {
        if (showArchived) {
            loadArchivedBases();
            loadResourceMetrics();
        }
    }, [showArchived, loadArchivedBases, loadResourceMetrics]);

    const handleUnarchive = async (base) => {
        const confirmed = await confirmModal(
            `Restaurer la base « ${base.name} » ?\n\nLes chunks seront re-indexés dans Weaviate depuis PostgreSQL. Coût LLM = $0.00.`,
            { confirmText: 'Restaurer', confirmStyle: 'primary' }
        );
        if (!confirmed) return;

        setUnarchiving(base.id);
        setUnarchiveProgress({ phase: 'start', label: 'Démarrage...' });

        try {
            const res = await fetch(`${API_BASE}/${base.id}/unarchive`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done: streamDone } = await reader.read();
                if (streamDone) break;
                buffer += decoder.decode(value, { stream: true });

                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.substring(0, idx);
                    buffer = buffer.substring(idx + 2);
                    const dataMatch = rawEvent.match(/^data:\s*(.+)/m);
                    if (!dataMatch) continue;
                    let payload;
                    try { payload = JSON.parse(dataMatch[1]); } catch { continue; }

                    if (payload.type === 'status') {
                        setUnarchiveProgress({ phase: payload.phase, label: payload.label });
                    } else if (payload.type === 'done') {
                        toast.success(`Base restaurée : ${payload.documentsReindexed} doc(s), ${payload.chunksIndexed} chunk(s) re-indexés`);
                    } else if (payload.type === 'error') {
                        throw new Error(payload.error);
                    }
                }
            }
            await loadBases();
            await loadArchivedBases();
        } catch (err) {
            toast.error(`Restauration : ${err.message}`);
        } finally {
            setUnarchiving(null);
            setUnarchiveProgress(null);
        }
    };

    const handlePurge = async (base) => {
        const confirmed = await confirmModal(
            `Supprimer DÉFINITIVEMENT la base « ${base.name} » ?\n\n⚠ Cette action est irréversible. Toutes les données (documents, résumés, scores) seront effacées.`,
            { danger: true, confirmText: 'Supprimer définitivement' }
        );
        if (!confirmed) return;

        try {
            const res = await fetch(`${API_BASE}/${base.id}/purge`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            toast.success(`Base « ${base.name} » supprimée définitivement`);
            await loadArchivedBases();
            await loadResourceMetrics();
        } catch (err) {
            toast.error(`Purge : ${err.message}`);
        }
    };

    // ─── Filtres / recherche / tri / groupement ───────────────────
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

    // Templates distincts présents (pour les chips de filtre)
    const templateFilters = React.useMemo(() => {
        const m = new Map();
        for (const b of bases) {
            const slug = b.template?.slug;
            if (slug && !m.has(slug)) m.set(slug, b.template?.label || b.template?.name || slug);
        }
        return [...m.entries()].map(([slug, label]) => ({ slug, label }));
    }, [bases]);

    // Application des filtres (onglet) + recherche texte
    const filteredBases = React.useMemo(() => {
        let arr = bases;
        if (activeFilter === 'system') arr = arr.filter(b => b.is_system);
        else if (activeFilter === 'models') arr = arr.filter(b => b.is_model);
        else if (activeFilter === 'mine') arr = arr.filter(b => b.user_role === 'owner');
        else if (activeFilter.startsWith('tpl:')) { const slug = activeFilter.slice(4); arr = arr.filter(b => b.template?.slug === slug); }
        const q = norm(search);
        if (q) arr = arr.filter(b => norm(b.name).includes(q) || norm(b.description).includes(q) || norm(b.category).includes(q));
        return arr;
    }, [bases, activeFilter, search]);

    const applySort = React.useCallback((arr) => {
        const a = [...arr];
        if (sortBy === 'name') a.sort((x, y) => x.name.localeCompare(y.name, 'fr'));
        else if (sortBy === 'docs') a.sort((x, y) => (y.document_count || 0) - (x.document_count || 0));
        else if (sortBy === 'score') a.sort((x, y) => (y.scoring_aggregate?.global_score || 0) - (x.scoring_aggregate?.global_score || 0));
        else a.sort((x, y) => new Date(y.updated_at || 0) - new Date(x.updated_at || 0)); // recent
        return a;
    }, [sortBy]);

    // Liste plate triée (mode liste dense)
    const sortedFlat = React.useMemo(() => applySort(filteredBases), [filteredBases, applySort]);

    // Groupement par catégorie (mode grille), tri intra-groupe
    const groupedBases = React.useMemo(() => {
        const groups = {};
        for (const base of filteredBases) {
            const cat = base.category || '';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(base);
        }
        const sorted = Object.entries(groups).sort(([a], [b]) => {
            if (!a) return 1;
            if (!b) return -1;
            return a.localeCompare(b, 'fr');
        });
        return sorted.map(([cat, list]) => [cat, applySort(list)]);
    }, [filteredBases, applySort]);

    const allCategoryNames = React.useMemo(() => {
        const names = new Set(bases.map(b => b.category).filter(Boolean));
        return [...names].sort((a, b) => a.localeCompare(b, 'fr'));
    }, [bases]);

    // Catégories inconnues → repliées par défaut (vue d'ensemble à l'ouverture)
    useEffect(() => {
        setCollapsedCategories(prev => {
            let changed = false; const next = { ...prev };
            for (const [cat] of groupedBases) {
                const k = cat || '__uncategorized__';
                if (!(k in next)) { next[k] = true; changed = true; }
            }
            return changed ? next : prev;
        });
    }, [groupedBases]);
    // Persistance du pliage
    useEffect(() => { try { localStorage.setItem(COLLAPSE_LS, JSON.stringify(collapsedCategories)); } catch {} }, [collapsedCategories, COLLAPSE_LS]);

    const toggleCategoryCollapse = (cat) => {
        setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
    };
    const setAllCollapsed = (val) => {
        setCollapsedCategories(() => {
            const next = {};
            for (const [cat] of groupedBases) next[cat || '__uncategorized__'] = val;
            return next;
        });
    };

    const handleAssignCategory = async (baseId, category) => {
        setCategoryAssigning(baseId);
        try {
            const res = await fetch(`${API_BASE}/categories/batch`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ base_ids: [baseId], category: category || null }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await loadBases();
        } catch (err) {
            toast.error(`Catégorie : ${err.message}`);
        } finally {
            setCategoryAssigning(null);
        }
    };

    // ─── Édition inline titre / description ────────────────────────
    const startEditField = (field) => {
        setEditingField(field);
        setEditValue(field === 'name' ? selectedBase.name : (selectedBase.description || ''));
    };
    const cancelEditField = () => { setEditingField(null); setEditValue(''); };
    const saveField = async () => {
        if (savingField || !editingField) return;
        const trimmed = editValue.trim();
        if (editingField === 'name' && !trimmed) { toast.warning('Le nom ne peut pas être vide'); return; }
        setSavingField(true);
        try {
            const body = {};
            body[editingField] = editingField === 'description' && !trimmed ? '' : trimmed;
            const res = await fetch(`${API_BASE}/${selectedBase.id}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            const updated = await res.json();
            setSelectedBase(prev => ({ ...prev, ...updated }));
            await loadBases();
            setEditingField(null);
            setEditValue('');
        } catch (err) {
            toast.error(`Sauvegarde : ${err.message}`);
        } finally {
            setSavingField(false);
        }
    };

    // ─── Actions ──────────────────────────────────────────────────
    const handleCreateBase = async (e) => {
        e.preventDefault();
        if (!createForm.name || !createForm.template_id) {
            toast.warning('Nom et template requis');
            return;
        }
        setCreating(true);
        try {
            const slug = createForm.slug || createForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            const res = await fetch(API_BASE, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    slug, name: createForm.name,
                    description: createForm.description,
                    template_id: createForm.template_id,
                    summarizer_model: createForm.summarizer_model || undefined,
                }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                throw new Error(errData.error || 'Construction échouée');
            }
            const newBase = await res.json();

            // Appliquer le canevas si sélectionné (PUT custom_summary_prompt)
            if (createForm.canevas_prompt) {
                try {
                    await fetch(`${API_BASE}/${newBase.id}`, {
                        method: 'PUT',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ custom_summary_prompt: createForm.canevas_prompt }),
                    });
                    newBase.custom_summary_prompt = createForm.canevas_prompt;
                } catch { /* non bloquant */ }
            }

            // Appliquer la catégorie si choisie (PUT category)
            if (createForm.category && createForm.category.trim()) {
                try {
                    await fetch(`${API_BASE}/${newBase.id}`, {
                        method: 'PUT',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ category: createForm.category.trim() }),
                    });
                    newBase.category = createForm.category.trim();
                } catch { /* non bloquant */ }
            }

            toast.success('Base construite');
            setShowCreateModal(false);
            setCreateForm({ name: '', slug: '', description: '', template_id: null, summarizer_model: '', canevas_prompt: '', category: '' });
            await loadBases();
            setSelectedBase(newBase);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setCreating(false);
        }
    };

    // ─── Canevas Oracle C3 — Bases Modèles ────────────────────
    const handlePromoteModel = async () => {
        if (!selectedBase || promoting) return;
        const confirmed = await confirmModal(
            `Promouvoir "${selectedBase.name}" comme modèle réutilisable ? Les autres utilisateurs pourront cloner sa configuration.`,
            { confirmText: 'Promouvoir', confirmStyle: 'primary' }
        );
        if (!confirmed) return;
        setPromoting(true);
        try {
            const res = await fetch(`${API_BASE}/${selectedBase.id}/promote-model`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            const data = await res.json();
            toast.success('Base promue comme modèle validé');
            setSelectedBase(prev => ({ ...prev, is_model: true, model_metadata: data.model_metadata, promoted_at: new Date().toISOString() }));
            await loadBases();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setPromoting(false);
        }
    };

    const handleDemoteModel = async () => {
        if (!selectedBase || promoting) return;
        const confirmed = await confirmModal(
            `Retirer le statut modèle de "${selectedBase.name}" ?`,
            { confirmText: 'Retirer', confirmStyle: 'danger' }
        );
        if (!confirmed) return;
        setPromoting(true);
        try {
            const res = await fetch(`${API_BASE}/${selectedBase.id}/promote-model`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success('Statut modèle retiré');
            setSelectedBase(prev => ({ ...prev, is_model: false }));
            await loadBases();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setPromoting(false);
        }
    };

    const handleCloneFromModel = async (modelBaseId, name, description) => {
        setCreating(true);
        try {
            const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            const res = await fetch(`${API_BASE}/clone-from-model`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ model_base_id: modelBaseId, slug, name, description }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            const newBase = await res.json();
            toast.success(`Base clonée depuis le modèle`);
            setShowCreateModal(false);
            setCreateForm({ name: '', slug: '', description: '', template_id: null, summarizer_model: '', canevas_prompt: '', category: '' });
            await loadBases();
            setSelectedBase(newBase);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setCreating(false);
        }
    };

    const doUpload = async (file) => {
        if (!file || !selectedBase) return;
        setUploadProgress({ phase: 'start', label: 'Envoi du fichier...', filename: file.name });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${API_BASE}/${selectedBase.id}/documents/upload`, {
                method: 'POST', headers: authHeaders(), body: formData,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let done = false;
            let finalResult = null;

            while (!done) {
                const { value, done: streamDone } = await reader.read();
                if (streamDone) break;
                buffer += decoder.decode(value, { stream: true });

                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.substring(0, idx);
                    buffer = buffer.substring(idx + 2);

                    const eventMatch = rawEvent.match(/^event:\s*(\S+)/m);
                    const dataMatch = rawEvent.match(/^data:\s*(.+)/m);
                    if (!dataMatch) continue;

                    let payload;
                    try { payload = JSON.parse(dataMatch[1]); } catch { continue; }
                    const eventType = eventMatch?.[1] || 'status';

                    if (eventType === 'status') {
                        setUploadProgress({
                            phase: payload.phase,
                            label: payload.label,
                            filename: file.name,
                        });
                    } else if (eventType === 'done') {
                        finalResult = payload;
                        done = true;
                    } else if (eventType === 'error') {
                        throw new Error(payload.error || 'Upload failed');
                    }
                }
            }

            setUploadProgress(null);
            if (finalResult) {
                toast.success(`${file.name} indexé`);
                await loadDocuments(selectedBase.id);
                await loadBases();
            }
        } catch (err) {
            setUploadProgress(null);
            toast.error(`Upload : ${err.message}`);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) await doUpload(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && !uploadProgress) await doUpload(file);
    };

    const handleDeleteDocument = async (docId, name) => {
        if (!await confirmModal(`Supprimer "${name}" ? Cette action est irréversible.`, { danger: true, confirmText: 'Supprimer' })) return;
        try {
            const res = await fetch(`${API_BASE}/${selectedBase.id}/documents/${docId}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success('Document supprimé');
            await loadDocuments(selectedBase.id);
            await loadBases();
        } catch (err) {
            toast.error(`Suppression : ${err.message}`);
        }
    };

    // Suppression d'une base entière (soft delete côté backend via archiveBase).
    // Garde-fous :
    //   - Seul l'owner peut archiver (403 sinon, propagé en toast explicite)
    //   - confirmModal obligatoire avec nom + compte de documents + mention
    //     soft delete réversible côté DB (archived_at) pour rassurer
    //   - Si la base est sélectionnée en vue détail au moment de la suppression,
    //     on désélectionne pour retomber sur la liste
    const handleDeleteBase = async (base, e) => {
        if (e) { e.stopPropagation(); e.preventDefault(); }
        const docCount = parseInt(base.document_count) || 0;
        const docLabel = docCount === 0
            ? 'Cette base ne contient aucun document.'
            : `Cette base contient ${docCount} document${docCount > 1 ? 's' : ''} qui deviendront inaccessibles.`;
        const confirmed = await confirmModal(
            `Archiver la base « ${base.name} » ?\n\n${docLabel}\n\nL'action est réversible (soft delete côté base) — contacte un administrateur pour restaurer.`,
            { danger: true, confirmText: 'Archiver la base' }
        );
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE}/${base.id}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                if (res.status === 403) {
                    throw new Error(errData.error || 'Seul le propriétaire peut archiver cette base.');
                }
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
            toast.success(`Base « ${base.name} » archivée`);
            if (selectedBase?.id === base.id) setSelectedBase(null);
            await loadBases();
        } catch (err) {
            toast.error(`Archivage : ${err.message}`);
        }
    };

    // Phase C1 bis — Override du modèle de synthèse par base (owner-only).
    // `value` = clé symbolique (kimi / sonnet / opus / gpt5-pro / deepseek-r1)
    // ou chaîne vide pour revenir au défaut template (PATCH body = null).
    // Appliqué aux DEUX niveaux (level1 + level2) en même temps — la priorité
    // sémantique est câblée dans les handlers (NoteHandler préfère level2).
    const handleSummarizerChange = async (value) => {
        if (!selectedBase) return;
        setSavingSummarizer(true);
        try {
            // Body : objet vide {} = reset au défaut template (le backend lit
            // req.body.level1_llm || level2_llm, sans quoi override devient null).
            // Envoi de `null` littéral refusé par body-parser en mode strict.
            const body = value
                ? { level1_llm: value, level2_llm: value }
                : {};
            const res = await fetch(`${API_BASE}/${selectedBase.id}/summarizer`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                if (res.status === 403) {
                    throw new Error(errData.error || 'Seul le propriétaire peut changer le modèle.');
                }
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
            const updated = await res.json();
            setSelectedBase(prev => prev ? { ...prev, summarizer_override: updated.summarizer_override ?? null } : prev);
            toast.success(value ? `Modèle de synthèse : ${value}` : 'Retour au modèle du template');
        } catch (err) {
            toast.error(`Modèle de synthèse : ${err.message}`);
        } finally {
            setSavingSummarizer(false);
        }
    };

    // Canevas Oracle C2 — Appliquer/retirer des directives de synthèse
    const [savingCanevas, setSavingCanevas] = useState(false);
    const handleCanevasApply = async (promptContent) => {
        if (!selectedBase) return;
        setSavingCanevas(true);
        try {
            const res = await fetch(`${API_BASE}/${selectedBase.id}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ custom_summary_prompt: promptContent || null }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
            const updated = await res.json();
            setSelectedBase(prev => prev ? { ...prev, custom_summary_prompt: updated.custom_summary_prompt ?? null } : prev);
            toast.success(promptContent ? 'Directives de synthèse appliquées' : 'Directives retirées');
        } catch (err) {
            toast.error(`Directives : ${err.message}`);
        } finally {
            setSavingCanevas(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════
    // VUE DÉTAIL D'UNE BASE
    // ═══════════════════════════════════════════════════════════════
    if (selectedBase) {
        const Icon = TEMPLATE_ICONS[selectedBase.template?.slug] || Database;
        const totalSize = documents.reduce((sum, d) => sum + (parseInt(d.size_bytes) || 0), 0);
        const doneCount = documents.filter(d => d.processing_status === 'done' || d.processing_status === 'indexed').length;

        // Get preview document for right panel
        const previewDoc = previewDocId ? documents.find(d => d.id === previewDocId) : null;
        const hasDoneDocuments = documents.some(d => d.processing_status === 'done' || d.processing_status === 'indexed');

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <style>{INLINE_CSS}</style>

                {/* Bouton retour discret */}
                <button
                    onClick={() => { setSelectedBase(null); setDocuments([]); }}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', alignSelf: 'flex-start',
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                        borderRadius: 8, cursor: 'pointer', color: 'var(--text-secondary)',
                        fontSize: 12, fontWeight: 500, transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT_BORDER; e.currentTarget.style.color = ACCENT; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                >
                    <ArrowLeft size={13} /> Retour aux bases
                </button>

                {/* ═══ 2-COLUMN LAYOUT: left content + right summary panel ═══ */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* MAIN CONTENT */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* ═══ HEADER PREMIUM ═══ */}
                <div style={{
                    position: 'relative', overflow: 'hidden',
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 16, padding: 24,
                    display: 'flex', alignItems: 'flex-start', gap: 20,
                }}>
                    {/* Accent gradient en fond */}
                    <div style={{
                        position: 'absolute', top: 0, right: 0, width: '40%', height: '100%',
                        background: `radial-gradient(ellipse at top right, ${ACCENT_TINT}, transparent 70%)`,
                        pointerEvents: 'none',
                    }} />

                    {/* Icône grande */}
                    <div style={{
                        width: 64, height: 64, borderRadius: 14,
                        background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59, 130, 246, 0.08))`,
                        border: `1px solid ${ACCENT_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, boxShadow: `0 0 20px ${ACCENT_GLOW}`,
                        position: 'relative', zIndex: 1,
                    }}>
                        <Icon size={28} style={{ color: ACCENT }} />
                    </div>

                    {/* Infos + actions */}
                    <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {editingField === 'name' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input
                                            autoFocus
                                            value={editValue}
                                            onChange={e => setEditValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') saveField(); if (e.key === 'Escape') cancelEditField(); }}
                                            maxLength={200}
                                            style={{
                                                flex: 1, fontSize: 20, fontWeight: 700, color: 'var(--text)',
                                                background: 'rgba(255,255,255,0.04)', border: `1px solid ${ACCENT_BORDER}`,
                                                borderRadius: 8, padding: '4px 10px', outline: 'none',
                                                fontFamily: 'inherit',
                                            }}
                                        />
                                        <button onClick={saveField} disabled={savingField} style={{
                                            padding: '4px 10px', fontSize: 11, fontWeight: 600,
                                            background: ACCENT, color: '#fff', border: 'none', borderRadius: 6,
                                            cursor: 'pointer',
                                        }}>
                                            {savingField ? <Loader size={12} className="expertise-spin" /> : <Check size={12} />}
                                        </button>
                                        <button onClick={cancelEditField} style={{
                                            padding: '4px 8px', fontSize: 11,
                                            background: 'transparent', color: 'var(--text-muted)',
                                            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                                            cursor: 'pointer',
                                        }}>Esc</button>
                                    </div>
                                ) : (
                                    <h1
                                        onClick={() => { if ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) startEditField('name'); }}
                                        title={(selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system ? 'Cliquer pour modifier le titre' : ''}
                                        style={{
                                            fontSize: 22, fontWeight: 700, color: 'var(--text)',
                                            margin: 0, lineHeight: 1.2,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            cursor: ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) ? 'text' : 'default',
                                            borderBottom: ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) ? '1px dashed transparent' : 'none',
                                            transition: 'border-color 0.2s',
                                        }}
                                        onMouseEnter={e => { if ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) e.currentTarget.style.borderBottomColor = ACCENT_BORDER; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderBottomColor = 'transparent'; }}
                                    >
                                        {selectedBase.name}
                                    </h1>
                                )}
                                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                    <div style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        padding: '3px 10px', borderRadius: 12,
                                        background: ACCENT_TINT, border: `1px solid ${ACCENT_BORDER}`,
                                        fontSize: 11, fontWeight: 600, color: ACCENT,
                                    }}>
                                        <Icon size={11} />
                                        {selectedBase.template?.label || selectedBase.template?.name || 'Template'}
                                    </div>
                                    {selectedBase.is_model && (
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            padding: '3px 10px', borderRadius: 12,
                                            background: 'rgba(245, 158, 11, 0.10)', border: '1px solid rgba(245, 158, 11, 0.30)',
                                            fontSize: 11, fontWeight: 700, color: '#f59e0b',
                                        }}>
                                            <Award size={11} /> Modèle validé
                                        </div>
                                    )}
                                    {selectedBase.cloned_from_base_id && (
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            padding: '3px 10px', borderRadius: 12,
                                            background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)',
                                            fontSize: 11, fontWeight: 600, color: '#10b981',
                                        }}>
                                            <GitBranch size={11} /> Cloné depuis modèle
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Bouton upload premium — visible uniquement pour owner/editor (le handler backend refuse les reader) */}
                            {(selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && (
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '10px 18px', flexShrink: 0,
                                    background: uploadProgress ? 'var(--card-border)' : `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
                                    color: 'white', borderRadius: 10,
                                    cursor: uploadProgress ? 'not-allowed' : 'pointer',
                                    fontSize: 13, fontWeight: 600,
                                    boxShadow: uploadProgress ? 'none' : `0 4px 14px ${ACCENT_GLOW}`,
                                    opacity: uploadProgress ? 0.6 : 1, transition: 'all 0.2s ease',
                                }}>
                                    <Upload size={14} /> Uploader un document
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleFileUpload}
                                        disabled={!!uploadProgress}
                                        accept=".pdf,.docx,.odt,.ods,.odp,.pptx,.xlsx,.txt,.md,.markdown,.json,.csv"
                                    />
                                </label>
                            )}
                        </div>

                        {editingField === 'description' ? (
                            <div style={{ marginTop: 8 }}>
                                <textarea
                                    autoFocus
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) saveField(); if (e.key === 'Escape') cancelEditField(); }}
                                    rows={3}
                                    maxLength={1000}
                                    placeholder="Description de la base..."
                                    style={{
                                        width: '100%', fontSize: 13, color: 'var(--text-secondary)',
                                        background: 'rgba(255,255,255,0.04)', border: `1px solid ${ACCENT_BORDER}`,
                                        borderRadius: 8, padding: '8px 10px', outline: 'none',
                                        fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical',
                                    }}
                                />
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                    <button onClick={saveField} disabled={savingField} style={{
                                        padding: '4px 12px', fontSize: 11, fontWeight: 600,
                                        background: ACCENT, color: '#fff', border: 'none', borderRadius: 6,
                                        cursor: 'pointer',
                                    }}>
                                        {savingField ? <Loader size={12} className="expertise-spin" /> : 'Enregistrer'}
                                    </button>
                                    <button onClick={cancelEditField} style={{
                                        padding: '4px 10px', fontSize: 11,
                                        background: 'transparent', color: 'var(--text-muted)',
                                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                                        cursor: 'pointer',
                                    }}>Annuler</button>
                                    <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto', alignSelf: 'center' }}>Ctrl+Enter pour sauver</span>
                                </div>
                            </div>
                        ) : (
                            <p
                                onClick={() => { if ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) startEditField('description'); }}
                                title={(selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system ? 'Cliquer pour modifier la description' : ''}
                                style={{
                                    fontSize: 13, color: selectedBase.description ? 'var(--text-secondary)' : 'var(--text-muted)',
                                    lineHeight: 1.5, margin: '8px 0 0 0',
                                    cursor: ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) ? 'text' : 'default',
                                    fontStyle: selectedBase.description ? 'normal' : 'italic',
                                    borderBottom: ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) ? '1px dashed transparent' : 'none',
                                    transition: 'border-color 0.2s',
                                }}
                                onMouseEnter={e => { if ((selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor') && !selectedBase.is_system) e.currentTarget.style.borderBottomColor = ACCENT_BORDER; }}
                                onMouseLeave={e => { e.currentTarget.style.borderBottomColor = 'transparent'; }}
                            >
                                {selectedBase.description || 'Ajouter une description...'}
                            </p>
                        )}

                        <div style={{
                            display: 'flex', gap: 16, marginTop: 14,
                            fontSize: 11, color: 'var(--text-muted)',
                        }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Calendar size={11} /> Créée le {formatDate(selectedBase.created_at)}
                            </span>
                            {selectedBase.user_role && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <Activity size={11} /> Rôle : <strong style={{ color: 'var(--text-secondary)' }}>{selectedBase.user_role}</strong>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ═══ RÉGLAGES AVANCÉS — MODÈLE DE SYNTHÈSE (owner only) ═══ */}
                {selectedBase.user_role === 'owner' && (
                    <SummarizerOverrideCard
                        base={selectedBase}
                        saving={savingSummarizer}
                        onChange={handleSummarizerChange}
                    />
                )}

                {/* ═══ DIRECTIVES DE SYNTHÈSE — CANEVAS ORACLE C2 (owner only) ═══ */}
                {selectedBase.user_role === 'owner' && (
                    <CanevasPickerCard
                        base={selectedBase}
                        saving={savingCanevas}
                        onApply={handleCanevasApply}
                    />
                )}

                {/* ═══ STATS CARDS ═══ */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    <StatCard icon={FileText} label="Documents" value={documents.length} color={ACCENT} />
                    <StatCard icon={CheckCircle2} label="Indexés" value={doneCount} color="#10b981" />
                    <StatCard icon={HardDrive} label="Taille totale" value={formatBytes(totalSize)} color="#3b82f6" />
                </div>

                {/* ═══ INTERFACE ÉQUIPE — MEMBRES ═══ */}
                <BaseTeamSection
                    baseId={selectedBase.id}
                    isOwner={selectedBase.user_role === 'owner'}
                />

                {/* ═══ BENCH v0.2 — SCORING ═══ */}
                {doneCount > 0 && (
                    <ScoreCard
                        baseId={selectedBase.id}
                        scoringAggregate={selectedBase.scoring_aggregate}
                        isOwner={selectedBase.user_role === 'owner'}
                    />
                )}

                {/* ═══ CANEVAS ORACLE C3 — PROMOUVOIR / RETIRER MODÈLE ═══ */}
                {selectedBase.user_role === 'owner' && doneCount > 0 && (
                    selectedBase.is_model ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '14px 16px', borderRadius: 12,
                            background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.20)',
                        }}>
                            <Award size={18} style={{ color: '#f59e0b', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>Base modèle validée</div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                    Score {selectedBase.model_metadata?.score_at_promotion?.toFixed(1)}/10 · {selectedBase.model_metadata?.structure_profile?.documents_count || '?'} doc(s) · Les autres utilisateurs peuvent cloner cette configuration
                                </div>
                            </div>
                            <button
                                onClick={handleDemoteModel}
                                disabled={promoting}
                                style={{
                                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                                    background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                                    color: '#ef4444', cursor: promoting ? 'not-allowed' : 'pointer',
                                    opacity: promoting ? 0.5 : 1,
                                }}
                            >
                                Retirer
                            </button>
                        </div>
                    ) : (selectedBase.scoring_aggregate?.global_score >= 8) ? (
                        <button
                            onClick={handlePromoteModel}
                            disabled={promoting}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                                background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
                                border: '1px solid rgba(245,158,11,0.25)',
                                color: '#f59e0b', cursor: promoting ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                transition: 'all 0.2s ease',
                                opacity: promoting ? 0.5 : 1,
                            }}
                        >
                            {promoting
                                ? <><Loader size={14} className="expertise-spin" /> Promotion en cours…</>
                                : <><Award size={15} /> Promouvoir en modèle réutilisable</>
                            }
                        </button>
                    ) : null
                )}

                {/* ═══ LEVEL3 — MÉTA-RÉSUMÉ DE CORPUS ═══ */}
                {doneCount >= 2 && (
                    <MetaSummaryCard
                        baseId={selectedBase.id}
                        baseName={selectedBase.name}
                        metaSummary={selectedBase.meta_summary}
                        documentCount={doneCount}
                        isOwner={selectedBase.user_role === 'owner'}
                    />
                )}

                {/* ═══ PROGRESS SSE ═══ */}
                {uploadProgress && <UploadProgressCard progress={uploadProgress} />}

                {/* ═══ LISTE DOCUMENTS ═══ */}
                {documents.length === 0 && !uploadProgress ? (
                    <div
                        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        style={{
                            padding: '48px 24px', textAlign: 'center',
                            background: isDragOver ? ACCENT_TINT : 'var(--card-bg)',
                            border: `2px dashed ${isDragOver ? ACCENT : 'var(--card-border)'}`,
                            borderRadius: 14, color: 'var(--text-muted)',
                            transition: 'all 0.2s ease', cursor: 'pointer',
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div style={{
                            width: 56, height: 56, borderRadius: 14, margin: '0 auto 14px',
                            background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.08))`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Upload size={26} style={{ color: ACCENT, opacity: 0.7 }} />
                        </div>
                        <p style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>
                            Cette base attend sa première source
                        </p>
                        <p style={{ margin: 0, fontSize: 12 }}>
                            Glisse un document ici — il sera découpé, analysé et indexé sémantiquement
                        </p>
                        <p style={{ margin: '4px 0 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                            PDF · DOCX · ODT · PPTX · XLSX · ODS · ODP · TXT · MD · JSON · CSV
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{
                            fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                            textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 4px',
                        }}>
                            Documents ({documents.length})
                        </div>
                        {documents.map(doc => <DocumentCard key={doc.id} doc={doc} onDelete={handleDeleteDocument} onSelect={setPreviewDocId} isSelected={previewDocId === doc.id} canDelete={selectedBase.user_role === 'owner' || selectedBase.user_role === 'editor'} />)}
                    </div>
                )}
                    </div>
                    {/* SUMMARY PANEL — Full width below documents */}
                    {hasDoneDocuments && <SummaryPreviewPanel doc={previewDoc} base={selectedBase} onRefresh={() => loadDocuments(selectedBase.id)} />}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════
    // VUE LISTE DES BASES
    // ═══════════════════════════════════════════════════════════════
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <style>{INLINE_CSS}</style>

            {/* ═══ HEADER LISTE (sticky — reste flottant en haut au scroll) ═══ */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 16, flexWrap: 'wrap',
                position: 'sticky', top: 0, zIndex: 10,
                background: 'var(--main-bg, #0a0d14)',
                padding: '12px 0',
                marginLeft: -12, marginRight: -12, paddingLeft: 12, paddingRight: 12,
                borderBottom: '1px solid var(--topbar-border, rgba(255,255,255,0.06))',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 11,
                        background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.08))`,
                        border: `1px solid ${ACCENT_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 0 16px ${ACCENT_GLOW}`,
                    }}>
                        <BookMarked size={20} style={{ color: ACCENT }} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                            Bases d'Expertise
                        </h2>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0 0' }}>
                            {bases.length} base{bases.length > 1 ? 's' : ''} accessible{bases.length > 1 ? 's' : ''} · {templates.length} template{templates.length > 1 ? 's' : ''} disponible{templates.length > 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {archivedBases.length > 0 && (
                        <button
                            onClick={() => setShowArchived(v => !v)}
                            title={showArchived ? 'Fermer le panneau Archives' : `Ouvrir le panneau Archives (${archivedBases.length})`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 14px',
                                background: 'rgba(239,68,68,0.08)',
                                border: '1px solid rgba(239,68,68,0.25)',
                                borderRadius: 10,
                                cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                color: '#ef4444',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.14)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <Archive size={13} /> Archives
                            <span style={{
                                background: 'rgba(239,68,68,0.25)', color: '#ef4444',
                                padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                            }}>{archivedBases.length}</span>
                        </button>
                    )}
                    <button
                        onClick={() => setShowCreateModal(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 18px',
                            background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
                            color: 'white', border: 'none', borderRadius: 10,
                            cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            boxShadow: `0 4px 14px ${ACCENT_GLOW}`,
                            transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 6px 20px ${ACCENT_GLOW}`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 4px 14px ${ACCENT_GLOW}`; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        <Plus size={15} /> Construire une base
                    </button>
                </div>
            </div>

            {/* ═══ BARRE D'OUTILS : recherche · filtres · vue · tri · classer IA ═══ */}
            {bases.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                {/* Recherche */}
                <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 180, maxWidth: 360 }}>
                    <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher une base…"
                        style={{
                            width: '100%', padding: '8px 30px 8px 32px', borderRadius: 10, fontSize: 13,
                            background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)',
                            border: '1px solid var(--card-border)', outline: 'none',
                        }}
                    />
                    {search && (
                        <button onClick={() => setSearch('')} title="Effacer"
                            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex' }}>
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Chips de filtre */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <button style={filterChip(activeFilter === 'all')} onClick={() => setActiveFilter('all')}>Toutes</button>
                    <button style={filterChip(activeFilter === 'system')} onClick={() => setActiveFilter('system')}><Lock size={11} /> Système</button>
                    <button style={filterChip(activeFilter === 'models')} onClick={() => setActiveFilter('models')}><Award size={11} /> Modèles</button>
                    <button style={filterChip(activeFilter === 'mine')} onClick={() => setActiveFilter('mine')}>Mes bases</button>
                    {templateFilters.map(t => (
                        <button key={t.slug} style={filterChip(activeFilter === `tpl:${t.slug}`)} onClick={() => setActiveFilter(`tpl:${t.slug}`)}>{t.label}</button>
                    ))}
                </div>

                <div style={{ flex: 1 }} />

                {/* Classer avec l'IA */}
                <button
                    onClick={() => setShowCategorizeModal(true)}
                    title="Proposer une catégorie pour chaque base avec l'IA"
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px',
                        borderRadius: 10, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                        background: 'rgba(139,92,246,0.10)', color: ACCENT, border: `1px solid ${ACCENT_BORDER}`,
                    }}
                >
                    <Wand2 size={14} /> Classer avec l'IA
                </button>

                {/* Tout déplier / replier (mode grille) */}
                {viewMode === 'grid' && (
                    <button onClick={() => setAllCollapsed(false)} title="Tout déplier"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 10, fontSize: 12, cursor: 'pointer', background: 'var(--card-bg)', color: 'var(--text-secondary)', border: '1px solid var(--card-border)' }}>
                        <ChevronDown size={13} /> Tout déplier
                    </button>
                )}

                {/* Bascule grille / liste */}
                <div style={{ display: 'inline-flex', border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden' }}>
                    <button onClick={() => setViewMode('grid')} title="Vue grille"
                        style={{ padding: '8px 10px', border: 'none', cursor: 'pointer', background: viewMode === 'grid' ? 'rgba(139,92,246,0.12)' : 'var(--card-bg)', color: viewMode === 'grid' ? ACCENT : 'var(--text-muted)', display: 'flex' }}>
                        <LayoutGrid size={15} />
                    </button>
                    <button onClick={() => setViewMode('list')} title="Vue liste compacte"
                        style={{ padding: '8px 10px', border: 'none', cursor: 'pointer', background: viewMode === 'list' ? 'rgba(139,92,246,0.12)' : 'var(--card-bg)', color: viewMode === 'list' ? ACCENT : 'var(--text-muted)', display: 'flex', borderLeft: '1px solid var(--card-border)' }}>
                        <List size={15} />
                    </button>
                </div>

                {/* Tri */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ArrowDownUp size={13} style={{ color: 'var(--text-muted)' }} />
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                        style={{ padding: '8px 10px', borderRadius: 10, fontSize: 12, background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', border: '1px solid var(--card-border)', cursor: 'pointer' }}>
                        <option value="recent">Récent</option>
                        <option value="name">Nom</option>
                        <option value="docs">Documents</option>
                        <option value="score">Score</option>
                    </select>
                </div>
            </div>
            )}

            {/* ═══ CONTAINER HYBRIDE : bases à gauche / archives à droite en mode split ═══ */}
            <div style={{
                display: 'flex',
                flexDirection: showArchived ? 'row' : 'column',
                gap: 20,
                alignItems: 'flex-start',
            }}>
            <div style={{ flex: showArchived ? '1 1 60%' : '1 1 100%', minWidth: 0, width: '100%' }}>

            {/* ═══ BASES GROUPÉES PAR CATÉGORIE ═══ */}
            {loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <Loader size={22} className="expertise-spin" />
                    <div style={{ fontSize: 12, marginTop: 10 }}>Chargement...</div>
                </div>
            ) : bases.length === 0 ? (
                <EmptyBasesState onCreate={() => setShowCreateModal(true)} />
            ) : filteredBases.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <Search size={22} style={{ opacity: .5 }} />
                    <div style={{ fontSize: 13, marginTop: 10 }}>Aucune base ne correspond à ta recherche / tes filtres.</div>
                    <button onClick={() => { setSearch(''); setActiveFilter('all'); }} style={{ marginTop: 14, ...filterChip(false) }}>Réinitialiser</button>
                </div>
            ) : viewMode === 'list' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {sortedFlat.map(base => (
                        <BaseRow key={base.id} base={base} onSelect={setSelectedBase} />
                    ))}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {groupedBases.map(([category, categoryBases]) => {
                        const catKey = category || '__uncategorized__';
                        const isCollapsed = collapsedCategories[catKey];
                        const catLabel = category || 'Non classé';
                        const catColor = category ? ACCENT : 'var(--text-muted)';

                        return (
                            <div key={catKey}>
                                {/* Header catégorie */}
                                <button
                                    onClick={() => toggleCategoryCollapse(catKey)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        padding: '6px 2px', marginBottom: isCollapsed ? 0 : 10,
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <ChevronDown size={14} style={{
                                        color: catColor,
                                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                    }} />
                                    {category ? (
                                        <FolderOpen size={14} style={{ color: catColor }} />
                                    ) : (
                                        <Tag size={13} style={{ color: catColor }} />
                                    )}
                                    <span style={{
                                        fontSize: 12, fontWeight: 600, color: catColor,
                                        textTransform: 'uppercase', letterSpacing: 0.8,
                                    }}>
                                        {catLabel}
                                    </span>
                                    <span style={{
                                        fontSize: 11, fontWeight: 500,
                                        padding: '1px 7px', borderRadius: 8,
                                        background: category ? `${ACCENT_TINT}` : 'rgba(255,255,255,0.04)',
                                        color: category ? ACCENT : 'var(--text-muted)',
                                        border: `1px solid ${category ? ACCENT_BORDER : 'rgba(255,255,255,0.06)'}`,
                                    }}>
                                        {categoryBases.length}
                                    </span>
                                </button>

                                {/* Grille des bases */}
                                {!isCollapsed && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                        gap: 14,
                                    }}>
                                        {categoryBases.map(base => (
                                            <BaseCard
                                                key={base.id}
                                                base={base}
                                                onSelect={setSelectedBase}
                                                onDelete={handleDeleteBase}
                                                allCategories={allCategoryNames}
                                                onCategoryChange={handleAssignCategory}
                                                categoryAssigning={categoryAssigning}
                                                onScoreBadgeClick={setScoreModalBase}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ═══ MODALE Classer avec l'IA ═══ */}
            {showCategorizeModal && (
                <CategorizeModal
                    bases={bases}
                    allCategories={allCategoryNames}
                    onClose={() => setShowCategorizeModal(false)}
                    onApplied={loadBases}
                />
            )}

            {/* ═══ MODALE BENCH — Score détaillé ═══ */}
            {scoreModalBase && (
                <div
                    onClick={() => setScoreModalBase(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 100000,
                        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: '100%', maxWidth: 520, maxHeight: '80vh',
                            overflowY: 'auto', padding: 4,
                        }}
                    >
                        <ScoreCard
                            baseId={scoreModalBase.id}
                            scoringAggregate={scoreModalBase.scoring_aggregate}
                            isOwner={scoreModalBase.user_role === 'owner'}
                        />
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                            <button
                                onClick={() => setScoreModalBase(null)}
                                style={{
                                    padding: '8px 28px', borderRadius: 10,
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.10)',
                                    color: 'var(--text-secondary)', fontSize: 13,
                                    fontWeight: 500, cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            </div>{/* fin colonne gauche (bases actives) */}

            {/* ═══ SECTION ADMIN — ARCHIVÉES + MÉTRIQUES (colonne droite en mode split) ═══ */}
            <div style={{
                flex: showArchived ? '0 0 420px' : '1 1 100%',
                width: showArchived ? 420 : 'auto',
                position: showArchived ? 'sticky' : 'static',
                top: showArchived ? 80 : 'auto',
                maxHeight: showArchived ? 'calc(100vh - 100px)' : 'none',
                overflowY: showArchived ? 'auto' : 'visible',
                borderTop: showArchived ? 'none' : '1px solid rgba(255,255,255,0.06)',
                borderLeft: showArchived ? '1px solid rgba(239,68,68,0.15)' : 'none',
                paddingLeft: showArchived ? 16 : 0,
                paddingTop: showArchived ? 0 : 16,
                marginTop: showArchived ? 0 : 8,
                background: showArchived ? 'rgba(239,68,68,0.03)' : 'transparent',
                borderRadius: showArchived ? 10 : 0,
            }}>
                <button
                    onClick={() => setShowArchived(!showArchived)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'var(--text-muted)', fontSize: 13, fontWeight: 500,
                        padding: '6px 0', transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                    <Archive size={14} />
                    Bases archivées
                    {archivedBases.length > 0 && (
                        <span style={{
                            background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                            padding: '1px 7px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        }}>{archivedBases.length}</span>
                    )}
                    <ChevronDown size={13} style={{
                        transform: showArchived ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                    }} />
                </button>

                {showArchived && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>

                        {/* Métriques ressources (admin only) */}
                        {isAdmin && resourceMetrics && (
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                gap: 10,
                            }}>
                                <MetricCard
                                    icon={<Database size={15} />}
                                    label="Bases actives"
                                    value={resourceMetrics.postgresql.active_bases}
                                    sub={`${resourceMetrics.postgresql.archived_bases} archivée(s)`}
                                />
                                <MetricCard
                                    icon={<FileText size={15} />}
                                    label="Documents actifs"
                                    value={resourceMetrics.postgresql.active_documents}
                                    sub={`${resourceMetrics.postgresql.soft_deleted_documents} supprimé(s)`}
                                />
                                <MetricCard
                                    icon={<Gauge size={15} />}
                                    label="Weaviate chunks"
                                    value={resourceMetrics.weaviate.total_chunks.toLocaleString()}
                                    sub={`~${resourceMetrics.summary.memory_usage_mb} MB RAM`}
                                />
                                <MetricCard
                                    icon={<HardDrive size={15} />}
                                    label="Stockage PG"
                                    value={`${resourceMetrics.summary.storage_usage_mb} MB`}
                                    sub={`${(resourceMetrics.postgresql.total_text_chars / 1000000).toFixed(1)}M chars`}
                                />
                            </div>
                        )}

                        {/* Liste bases archivées */}
                        {archivedBases.length === 0 ? (
                            <div style={{
                                padding: 24, textAlign: 'center',
                                color: 'var(--text-muted)', fontSize: 13,
                                border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 10,
                            }}>
                                Aucune base archivée
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {archivedBases.map(base => (
                                    <ArchivedBaseRow
                                        key={base.id}
                                        base={base}
                                        isAdmin={isAdmin}
                                        onUnarchive={handleUnarchive}
                                        onPurge={handlePurge}
                                        unarchiving={unarchiving}
                                        unarchiveProgress={unarchiveProgress}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>{/* fin colonne droite archives */}
            </div>{/* fin container hybride split */}

            {/* ═══ MODAL CONSTRUCTION ═══ */}
            {showCreateModal && (
                <ConfirmBuildBaseModal
                    templates={templates}
                    form={createForm}
                    setForm={setCreateForm}
                    onSubmit={handleCreateBase}
                    onClose={() => setShowCreateModal(false)}
                    creating={creating}
                    onCloneFromModel={handleCloneFromModel}
                    allCategories={allCategoryNames}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// SOUS-COMPOSANTS
// ═══════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// CanevasPickerCard — Directives de synthèse (Canevas Oracle C2)
// ─────────────────────────────────────────────────────────────
// Permet de sélectionner un canevas pré-construit ou d'éditer
// le prompt de synthèse manuellement. Le canevas sélectionné
// est copié dans custom_summary_prompt de la base.
// ────────────────────────────────────────────��────────────────

const CANEVAS_ICONS = { BookOpen, FlaskConical, Users, FileText };

function CanevasPickerCard({ base, onApply, saving }) {
    const [canevasList, setCanevasList] = useState([]);
    const [loadingCanevas, setLoadingCanevas] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(base?.custom_summary_prompt || '');
    const [showPicker, setShowPicker] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/canevas`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(data => { if (!cancelled) { setCanevasList(data); setLoadingCanevas(false); } })
            .catch(() => { if (!cancelled) setLoadingCanevas(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        setEditText(base?.custom_summary_prompt || '');
    }, [base?.custom_summary_prompt]);

    const applyCanevas = (canevas) => {
        setShowPicker(false);
        onApply(canevas.prompt_content);
    };

    const saveManual = () => {
        setEditing(false);
        onApply(editText);
    };

    const clearPrompt = () => {
        setEditing(false);
        setEditText('');
        onApply(null);
    };

    const currentPrompt = base?.custom_summary_prompt;

    return (
        <div style={{
            padding: 16, background: 'var(--card-bg)',
            border: '1px solid var(--card-border)', borderRadius: 12,
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'rgba(217, 119, 6, 0.08)', border: '1px solid rgba(217, 119, 6, 0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <BookOpen size={15} style={{ color: '#d97706' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        Directives de synthèse
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                        Oriente le modèle vers les aspects importants de tes documents
                    </div>
                </div>
                {saving && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#d97706' }}>
                        <Loader size={12} className="expertise-spin" /> Enregistrement…
                    </span>
                )}
            </div>

            {/* Prompt actuel */}
            {currentPrompt && !editing ? (
                <div style={{
                    padding: '10px 12px', borderRadius: 8,
                    background: 'rgba(217, 119, 6, 0.06)', border: '1px solid rgba(217, 119, 6, 0.15)',
                    fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5,
                    whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto',
                }}>
                    {currentPrompt}
                </div>
            ) : editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        rows={5}
                        style={{
                            width: '100%', padding: '10px 12px', fontSize: 12,
                            background: 'var(--input-bg)', color: 'var(--text)',
                            border: `1px solid rgba(217, 119, 6, 0.3)`, borderRadius: 8,
                            fontFamily: 'monospace', resize: 'vertical', minHeight: 100,
                            outline: 'none', boxSizing: 'border-box', lineHeight: 1.5,
                        }}
                        placeholder="Directives personnalisées pour guider la synthèse de tes documents..."
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => { setEditing(false); setEditText(currentPrompt || ''); }}
                            style={{ padding: '6px 12px', fontSize: 11, background: 'transparent', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer' }}>
                            Annuler
                        </button>
                        <button onClick={saveManual} disabled={saving}
                            style={{ padding: '6px 12px', fontSize: 11, background: '#d97706', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                            Sauvegarder
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Actions */}
            {!editing && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => setShowPicker(!showPicker)}
                        style={{
                            padding: '7px 12px', fontSize: 11, fontWeight: 600,
                            background: 'rgba(217, 119, 6, 0.08)', color: '#d97706',
                            border: '1px solid rgba(217, 119, 6, 0.25)', borderRadius: 7,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                        <BookOpen size={12} /> {currentPrompt ? 'Changer de canevas' : 'Appliquer un canevas'}
                    </button>
                    <button onClick={() => { setEditing(true); setEditText(currentPrompt || ''); }}
                        style={{
                            padding: '7px 12px', fontSize: 11, fontWeight: 500,
                            background: 'transparent', color: 'var(--text-secondary)',
                            border: '1px solid var(--card-border)', borderRadius: 7,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                        <Pencil size={11} /> {currentPrompt ? 'Modifier manuellement' : 'Écrire des directives'}
                    </button>
                    {currentPrompt && (
                        <button onClick={clearPrompt}
                            style={{
                                padding: '7px 12px', fontSize: 11, fontWeight: 500,
                                background: 'transparent', color: 'var(--text-muted)',
                                border: '1px solid var(--card-border)', borderRadius: 7,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                            <Trash2 size={11} /> Retirer
                        </button>
                    )}
                </div>
            )}

            {/* Picker dropdown */}
            {showPicker && (
                <div style={{
                    display: 'flex', flexDirection: 'column', gap: 6,
                    padding: '10px 0', borderTop: '1px solid var(--card-border)',
                }}>
                    {loadingCanevas ? (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: 8, textAlign: 'center' }}>
                            <Loader size={12} className="expertise-spin" /> Chargement…
                        </div>
                    ) : canevasList.length === 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: 8, textAlign: 'center' }}>
                            Aucun canevas disponible
                        </div>
                    ) : canevasList.map(c => {
                        const CIcon = CANEVAS_ICONS[c.icon] || FileText;
                        return (
                            <div key={c.id} onClick={() => applyCanevas(c)}
                                style={{
                                    padding: '10px 12px', borderRadius: 8,
                                    border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                                    cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                                    transition: 'all 0.12s ease',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.background = 'rgba(217, 119, 6, 0.04)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.background = 'var(--input-bg)'; }}
                            >
                                <CIcon size={14} style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                                        {c.name}
                                        {c.is_system && (
                                            <span style={{ fontSize: 9, fontWeight: 600, marginLeft: 6, padding: '1px 5px', borderRadius: 3, background: 'rgba(217, 119, 6, 0.1)', color: '#d97706' }}>
                                                Officiel
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                                        {c.description}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// SummarizerOverrideCard — Réglage avancé "Modèle de synthèse"
// ─────────────────────────────────────────────────────────────
// Phase C1 bis — Livrable 2. Owner-only. Cascade côté backend :
// base.summarizer_override > template.summary_tree > null.
// S'applique aux prochains uploads uniquement (pas de ré-indexation
// rétroactive).
// ─────────────────────────────────────────────────────────────
function SummarizerOverrideCard({ base, saving, onChange }) {
    const [benchHelpOpen, setBenchHelpOpen] = useState(false);
    const override = base?.summarizer_override || null;
    const templateDefault = base?.template?.pipeline_config?.summary_tree?.level1_llm || null;
    const currentValue = override?.level1_llm || '';

    return (
        <div style={{
            padding: 16, background: 'var(--card-bg)',
            border: '1px solid var(--card-border)', borderRadius: 12,
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: ACCENT_TINT, border: `1px solid ${ACCENT_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Sparkles size={15} style={{ color: ACCENT }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        Intelligence de synthèse
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                        Le modèle qui structure, analyse et condense chaque section de tes documents
                    </div>
                </div>
                {saving && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: ACCENT }}>
                        <Loader size={12} className="expertise-spin" /> Enregistrement…
                    </span>
                )}
            </div>

            <SummarizerModelPicker
                value={currentValue}
                onChange={onChange}
                disabled={saving}
                templateDefault={templateDefault || 'kimi'}
            />

            <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                Chaque nouveau document sera découpé, indexé sémantiquement et synthétisé par ce modèle. Les documents existants conservent leur synthèse actuelle.
                <br />
                <span
                    onClick={() => setBenchHelpOpen(true)}
                    style={{ opacity: 0.7, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                >
                    Source : ITRIIA BENCH 2026-04-12 · 1 doc · 1 évaluateur
                </span>
            </div>

            <BenchHelpModal isOpen={benchHelpOpen} onClose={() => setBenchHelpOpen(false)} />
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div style={{
            padding: 16, background: 'var(--card-bg)',
            border: '1px solid var(--card-border)', borderRadius: 12,
            display: 'flex', alignItems: 'center', gap: 12,
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 9,
                background: `${color}15`, border: `1px solid ${color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                <Icon size={17} style={{ color }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 500 }}>
                    {label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, marginTop: 2 }}>
                    {value}
                </div>
            </div>
        </div>
    );
}

// Modale « Classer avec l'IA » — l'IA propose une catégorie par base, Soufari confirme/ajuste
function CategorizeModal({ bases, allCategories, onClose, onApplied }) {
    const editable = React.useMemo(
        () => bases.filter(b => (b.user_role === 'owner' || b.user_role === 'editor') && !b.is_system),
        [bases]
    );
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]); // { base, target }
    const [applying, setApplying] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/categorize/suggest`, {
                    method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: '{}',
                });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
                const data = await res.json();
                const map = new Map((data.suggestions || []).map(s => [String(s.id), s.suggested_category]));
                if (!alive) return;
                setRows(editable.map(b => ({ base: b, target: (map.get(String(b.id)) || b.category || '').trim(), ai: map.get(String(b.id)) || '' })));
            } catch (e) { if (alive) setErr(e.message); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [editable]);

    const options = React.useMemo(
        () => [...new Set([...SUGGESTED_CATEGORIES, ...allCategories, ...rows.map(r => r.target).filter(Boolean)])].sort((a, b) => a.localeCompare(b, 'fr')),
        [allCategories, rows]
    );
    const setTarget = (id, val) => setRows(rs => rs.map(r => r.base.id === id ? { ...r, target: val } : r));
    const changes = rows.filter(r => (r.target || '').trim() !== (r.base.category || '')).length;

    const apply = async () => {
        setApplying(true);
        try {
            const byCat = new Map();
            for (const r of rows) {
                const cur = r.base.category || '';
                const tgt = (r.target || '').trim();
                if (tgt === cur) continue;
                if (!byCat.has(tgt)) byCat.set(tgt, []);
                byCat.get(tgt).push(r.base.id);
            }
            if (byCat.size === 0) { toast.info('Aucun changement à appliquer'); setApplying(false); return; }
            for (const [cat, ids] of byCat) {
                const res = await fetch(`${API_BASE}/categories/batch`, {
                    method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ base_ids: ids, category: cat || null }),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
            }
            toast.success(`${changes} base(s) classée(s)`);
            await onApplied();
            onClose();
        } catch (e) { toast.error(`Classement : ${e.message}`); }
        finally { setApplying(false); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100001, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 'min(640px,100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'var(--card-bg, #12151c)', border: '1px solid var(--card-border)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 30px 80px -30px rgba(0,0,0,.8)' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: '1px solid var(--card-border)' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(139,92,246,0.12)', border: `1px solid ${ACCENT_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Wand2 size={17} style={{ color: ACCENT }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Classer avec l'IA</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>L'IA propose une catégorie par base — ajuste puis applique.</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex' }}><X size={18} /></button>
                </div>

                {/* Corps */}
                <div style={{ padding: 14, overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ padding: 36, textAlign: 'center', color: 'var(--text-muted)' }}>
                            <Loader size={22} className="expertise-spin" />
                            <div style={{ fontSize: 13, marginTop: 12 }}>L'IA analyse tes bases…</div>
                        </div>
                    ) : err ? (
                        <div style={{ padding: 24, textAlign: 'center', color: '#ef4444', fontSize: 13 }}>⚠ {err}</div>
                    ) : rows.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Aucune base éditable à classer.</div>
                    ) : (
                        <>
                            <datalist id="categorize-cats">{options.map(o => <option key={o} value={o} />)}</datalist>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {rows.map(r => {
                                    const changed = (r.target || '').trim() !== (r.base.category || '');
                                    return (
                                        <div key={r.base.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 9, background: changed ? 'rgba(139,92,246,0.06)' : 'transparent', border: `1px solid ${changed ? ACCENT_BORDER : 'transparent'}` }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.base.name}</div>
                                                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{r.base.category ? `actuel : ${r.base.category}` : 'non classé'}</div>
                                            </div>
                                            <ArrowDownUp size={12} style={{ color: 'var(--text-muted)', transform: 'rotate(90deg)', flexShrink: 0 }} />
                                            <input list="categorize-cats" value={r.target} onChange={e => setTarget(r.base.id, e.target.value)} placeholder="Non classé"
                                                style={{ width: 190, flexShrink: 0, padding: '7px 10px', borderRadius: 8, fontSize: 12.5, background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', border: `1px solid ${changed ? ACCENT : 'var(--card-border)'}`, outline: 'none' }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--card-border)' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{changes} changement{changes > 1 ? 's' : ''}</span>
                    <div style={{ flex: 1 }} />
                    <button onClick={onClose} disabled={applying} style={{ padding: '9px 14px', borderRadius: 9, fontSize: 13, cursor: 'pointer', background: 'var(--card-bg)', color: 'var(--text-secondary)', border: '1px solid var(--card-border)' }}>Annuler</button>
                    <button onClick={apply} disabled={applying || loading || changes === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: applying || changes === 0 ? 'not-allowed' : 'pointer', background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`, color: '#fff', border: 'none', opacity: applying || changes === 0 ? 0.5 : 1 }}>
                        {applying ? <Loader size={14} className="expertise-spin" /> : <Check size={14} />} Appliquer
                    </button>
                </div>
            </div>
        </div>
    );
}

// Ligne compacte — mode liste dense (scannabilité à l'échelle)
function BaseRow({ base, onSelect }) {
    const Icon = TEMPLATE_ICONS[base.template?.slug] || Database;
    const score = base.scoring_aggregate?.global_score;
    return (
        <div onClick={() => onSelect(base)} className="expertise-base-card"
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
                background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.05))`, border: `1px solid ${ACCENT_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={15} style={{ color: ACCENT }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{base.name}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .3 }}>{base.template?.label || base.template?.name || 'Template'}</div>
            </div>
            {base.is_model && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)', display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}><Award size={8} /> MODÈLE</span>}
            {base.is_system && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(168,138,82,0.12)', color: '#A88A52', border: '1px solid rgba(168,138,82,0.30)', display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}><Lock size={8} /> SYSTÈME</span>}
            {base.category
                ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'rgba(139,92,246,0.10)', color: ACCENT, border: `1px solid ${ACCENT_BORDER}`, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}><FolderOpen size={10} />{base.category}</span>
                : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, color: 'var(--text-muted)', border: '1px solid var(--card-border)', flexShrink: 0 }}>Non classé</span>}
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0, minWidth: 60, textAlign: 'right' }}>{base.document_count || 0} doc{(base.document_count || 0) > 1 ? 's' : ''}</span>
            {score > 0 && <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0, minWidth: 28, textAlign: 'right', color: score >= 8 ? '#22c55e' : score >= 5 ? '#f59e0b' : '#ef4444' }}>{Number(score).toFixed(1)}</span>}
        </div>
    );
}

function BaseCard({ base, onSelect, onDelete, allCategories = [], onCategoryChange, categoryAssigning, onScoreBadgeClick }) {
    const Icon = TEMPLATE_ICONS[base.template?.slug] || Database;
    const isOwner = base.user_role === 'owner';
    const isEditor = base.user_role === 'editor';
    const isSystem = !!base.is_system;
    // Session Greg · bases système (is_system=true) figées pour non-admin
    const canEdit = (isOwner || isEditor) && !isSystem;
    const [showCatPicker, setShowCatPicker] = useState(false);
    const catRef = useRef(null);

    // Fermer le picker au clic extérieur
    useEffect(() => {
        if (!showCatPicker) return;
        const handler = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setShowCatPicker(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showCatPicker]);

    return (
        <div
            onClick={() => onSelect(base)}
            className="expertise-base-card"
            style={{
                padding: 18, background: 'var(--card-bg)',
                border: '1px solid var(--card-border)', borderRadius: 13,
                cursor: 'pointer', transition: 'all 0.2s ease',
                display: 'flex', flexDirection: 'column', gap: 12,
                position: 'relative', overflow: 'hidden',
            }}
        >
            {/* Bouton archiver (visible uniquement pour l'owner) */}
            {isOwner && onDelete && (
                <button
                    type="button"
                    onClick={(e) => onDelete(base, e)}
                    title="Archiver cette base"
                    aria-label={`Archiver la base ${base.name}`}
                    style={{
                        position: 'absolute', top: 10, right: 10,
                        width: 28, height: 28, borderRadius: 7,
                        background: 'var(--input-bg)',
                        border: '1px solid var(--card-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'all 0.15s ease',
                        color: 'var(--text-muted)', padding: 0,
                        zIndex: 2,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                        e.currentTarget.style.color = '#ef4444';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--input-bg)';
                        e.currentTarget.style.borderColor = 'var(--card-border)';
                        e.currentTarget.style.color = 'var(--text-muted)';
                    }}
                >
                    <Trash2 size={14} />
                </button>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingRight: isOwner ? 34 : 0 }}>
                <div style={{
                    width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                    background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.05))`,
                    border: `1px solid ${ACCENT_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={19} style={{ color: ACCENT }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 14, fontWeight: 600, color: 'var(--text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {base.name}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                        <span style={{
                            fontSize: 10, color: ACCENT, fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: 0.3,
                        }}>
                            {base.template?.label || base.template?.name || 'Template'}
                        </span>
                        {base.is_model && (
                            <span style={{
                                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                border: '1px solid rgba(245,158,11,0.25)',
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                                <Award size={8} /> MODÈLE
                            </span>
                        )}
                        {isSystem && (
                            <span
                                title="Base système — contenu validé, édition réservée aux administrateurs"
                                style={{
                                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                    background: 'rgba(168,138,82,0.12)', color: '#A88A52',
                                    border: '1px solid rgba(168,138,82,0.30)',
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    letterSpacing: 0.4,
                                }}
                            >
                                <Lock size={8} /> SYSTÈME
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {base.description && (
                <p style={{
                    fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}>
                    {base.description}
                </p>
            )}

            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 10,
                borderTop: '1px solid var(--card-border)',
                flexWrap: 'wrap',
            }}>
                {/* Badge nb de doc — pill vert fin (identique à "ITRIIA Cloud" de FilesInterface) */}
                <span style={{
                    padding: '2px 8px', fontSize: 10, borderRadius: 12,
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#22c55e',
                }}>
                    {base.document_count || 0} doc{(base.document_count || 0) > 1 ? 's' : ''}
                </span>
                {/* Rôle owner — texte orangé simple, pas d'encadré */}
                {base.user_role === 'owner' && (
                    <span style={{
                        color: '#f59e0b',
                        fontSize: 10, fontWeight: 500,
                    }}>
                        owner
                    </span>
                )}
                {/* Rôles non-owner — masqué sur base système (user n'a pas les droits effectifs) */}
                {base.user_role && base.user_role !== 'owner' && !isSystem && (
                    <span style={{
                        color: '#a78bfa',
                        fontSize: 10, fontWeight: 500,
                    }}>
                        {base.user_role}
                    </span>
                )}
                {/* Catégorie — tag cliquable pour changer */}
                {canEdit && onCategoryChange && (
                    <div ref={catRef} style={{ position: 'relative' }}>
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowCatPicker(!showCatPicker); }}
                            disabled={categoryAssigning === base.id}
                            title="Changer la catégorie"
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '2px 7px', fontSize: 10, fontWeight: 500,
                                background: base.category ? ACCENT_TINT : 'rgba(255,255,255,0.03)',
                                color: base.category ? ACCENT : 'var(--text-muted)',
                                border: `1px solid ${base.category ? ACCENT_BORDER : 'rgba(255,255,255,0.06)'}`,
                                borderRadius: 8, cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <Tag size={9} />
                            {categoryAssigning === base.id ? '...' : (base.category || 'Classer')}
                        </button>
                        {showCatPicker && (
                            <div style={{
                                position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                                borderRadius: 10, padding: 6, minWidth: 160,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                                zIndex: 20, display: 'flex', flexDirection: 'column', gap: 2,
                            }} onClick={e => e.stopPropagation()}>
                                {allCategories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => { onCategoryChange(base.id, cat); setShowCatPicker(false); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '5px 8px', fontSize: 11, fontWeight: base.category === cat ? 600 : 400,
                                            color: base.category === cat ? ACCENT : 'var(--text)',
                                            background: base.category === cat ? ACCENT_TINT : 'transparent',
                                            border: 'none', borderRadius: 6, cursor: 'pointer',
                                            textAlign: 'left', width: '100%',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = ACCENT_TINT}
                                        onMouseLeave={e => e.currentTarget.style.background = base.category === cat ? ACCENT_TINT : 'transparent'}
                                    >
                                        <FolderOpen size={11} /> {cat}
                                    </button>
                                ))}
                                {/* Nouvelle catégorie */}
                                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 2, paddingTop: 4 }}>
                                    <input
                                        placeholder="Nouvelle catégorie..."
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                onCategoryChange(base.id, e.target.value.trim());
                                                setShowCatPicker(false);
                                            }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        style={{
                                            width: '100%', padding: '4px 8px', fontSize: 11,
                                            background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(255,255,255,0.08)`,
                                            borderRadius: 6, color: 'var(--text)', outline: 'none',
                                            fontFamily: 'inherit',
                                        }}
                                    />
                                </div>
                                {/* Retirer la catégorie */}
                                {base.category && (
                                    <button
                                        onClick={() => { onCategoryChange(base.id, null); setShowCatPicker(false); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '5px 8px', fontSize: 11, color: '#ef4444',
                                            background: 'transparent', border: 'none', borderRadius: 6,
                                            cursor: 'pointer', textAlign: 'left', width: '100%',
                                            marginTop: 2,
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        Retirer la catégorie
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* BENCH v0.2 — Score compact (cliquable → modale détails) */}
                {base.scoring_aggregate?.global_score > 0 && (
                    <div
                        style={{ marginLeft: 'auto', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); onScoreBadgeClick?.(base); }}
                        title={`BENCH ${base.scoring_aggregate.global_score.toFixed(1)}/10 — cliquer pour détails`}
                    >
                        <ScoreCard baseId={base.id} scoringAggregate={base.scoring_aggregate} compact />
                    </div>
                )}
            </div>
        </div>
    );
}

function DocumentCard({ doc, onDelete, onSelect, isSelected, canDelete = true }) {
    const Icon = getFileIcon(doc.original_name);
    const isDone = doc.processing_status === 'done' || doc.processing_status === 'indexed';

    return (
        <div
            onClick={() => isDone && onSelect?.(doc.id)}
            className="expertise-doc-card" style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: 14, background: 'var(--card-bg)',
                border: isSelected ? `2px solid ${ACCENT}` : '1px solid var(--card-border)',
                borderRadius: 11,
                transition: 'all 0.15s ease',
                cursor: isDone ? 'pointer' : 'default',
                opacity: isDone ? 1 : 0.6,
            }}
            onMouseEnter={e => isDone && (e.currentTarget.style.background = 'rgba(139,92,246,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--card-bg)')}
        >
            <div style={{
                width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                background: ACCENT_TINT, border: `1px solid ${ACCENT_BORDER}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon size={17} style={{ color: ACCENT }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {doc.original_name}
                </div>
                <div style={{
                    fontSize: 11, color: 'var(--text-muted)',
                    display: 'flex', gap: 8, marginTop: 3, alignItems: 'center',
                }}>
                    <span style={{
                        padding: '1px 7px', borderRadius: 4,
                        background: 'var(--input-bg)', color: 'var(--text-secondary)',
                        fontWeight: 500, fontSize: 10,
                    }}>
                        {doc.gabarit || 'auto'}
                    </span>
                    <span>{formatBytes(parseInt(doc.size_bytes) || 0)}</span>
                    <span>·</span>
                    <StatusBadge status={doc.processing_status} />
                    {parseInt(doc.edited_sections_count) > 0 && (
                        <>
                            <span>·</span>
                            <span style={{
                                fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                                background: 'rgba(217, 119, 6, 0.1)', color: '#d97706',
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                                <Pencil size={7} /> {doc.edited_sections_count} modif.
                            </span>
                        </>
                    )}
                    <span>·</span>
                    <span>{formatDate(doc.created_at)}</span>
                </div>
            </div>
            {canDelete && (
                <button
                    onClick={() => onDelete(doc.id, doc.original_name)}
                    className="expertise-doc-delete"
                    style={{
                        background: 'transparent', border: '1px solid transparent',
                        cursor: 'pointer', color: 'var(--text-muted)',
                        padding: 8, borderRadius: 7, transition: 'all 0.15s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    title="Supprimer"
                >
                    <Trash2 size={15} />
                </button>
            )}
        </div>
    );
}

function UploadProgressCard({ progress }) {
    return (
        <div style={{
            padding: 16, borderRadius: 12,
            background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.05))`,
            border: `1px solid ${ACCENT_BORDER}`,
            display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: `0 0 20px ${ACCENT_GLOW}`,
        }}>
            <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: 'rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Loader size={18} className="expertise-spin" style={{ color: ACCENT }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {progress.filename}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {progress.label}
                </div>
                <div style={{
                    height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2,
                    overflow: 'hidden', marginTop: 8,
                }}>
                    <div className="expertise-progress-bar" style={{
                        height: '100%', width: '40%',
                        background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DARK})`,
                        borderRadius: 2,
                    }} />
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const configs = {
        done: { color: '#10b981', label: 'Prête', bg: 'rgba(16,185,129,0.12)' },
        indexed: { color: '#10b981', label: 'Indexé', bg: 'rgba(16,185,129,0.12)' },
        extracted: { color: '#3b82f6', label: 'Extrait', bg: 'rgba(59,130,246,0.12)' },
        chunked: { color: '#3b82f6', label: 'Découpé', bg: 'rgba(59,130,246,0.12)' },
        uploaded: { color: '#f59e0b', label: 'En attente', bg: 'rgba(245,158,11,0.12)' },
        failed: { color: '#ef4444', label: 'Erreur', bg: 'rgba(239,68,68,0.12)' },
    };
    const cfg = configs[status] || { color: '#6b7280', label: status || '—', bg: 'rgba(107,114,128,0.12)' };
    return (
        <span style={{
            padding: '1px 7px', borderRadius: 4,
            background: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 10,
        }}>
            {cfg.label}
        </span>
    );
}

function EmptyBasesState({ onCreate }) {
    return (
        <div style={{
            padding: '56px 28px', textAlign: 'center',
            background: 'var(--card-bg)',
            border: '1px dashed var(--card-border)', borderRadius: 14,
        }}>
            <div style={{
                width: 68, height: 68, borderRadius: 16, margin: '0 auto 18px',
                background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.08))`,
                border: `1px solid ${ACCENT_BORDER}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 0 24px ${ACCENT_GLOW}`,
            }}>
                <BookMarked size={30} style={{ color: ACCENT }} />
            </div>
            <p style={{ margin: '0 0 6px 0', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                Aucune base d'expertise
            </p>
            <p style={{ margin: '0 0 18px 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
                Construis un actif de connaissance structuré et évolutif à partir de tes documents. Chaque source est découpée, analysée sémantiquement et indexée — prête à être exploitée, affinée ou enrichie dans le temps.
                Les bases deviennent attachables à tes projets avec 3 modes d'injection.
            </p>
            <button
                onClick={onCreate}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 20px',
                    background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
                    color: 'white', border: 'none', borderRadius: 10,
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    boxShadow: `0 4px 14px ${ACCENT_GLOW}`,
                }}
            >
                <Plus size={15} /> Construire ma première base
            </button>
        </div>
    );
}

function ConfirmBuildBaseModal({ templates, form, setForm, onSubmit, onClose, creating, onCloneFromModel, allCategories = [] }) {
    const [quota, setQuota] = useState(null);
    const [quotaLoading, setQuotaLoading] = useState(true);
    const [modelBases, setModelBases] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(true);
    const [cloneMode, setCloneMode] = useState(null); // null or model object
    const [cloneName, setCloneName] = useState('');
    const [cloneDesc, setCloneDesc] = useState('');

    useEffect(() => {
        let cancelled = false;
        setQuotaLoading(true);
        fetch(`${API_BASE}/quota`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!cancelled) { setQuota(data); setQuotaLoading(false); } })
            .catch(() => { if (!cancelled) setQuotaLoading(false); });
        // Charger les modèles disponibles
        fetch(`${API_BASE}/models/list`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(data => { if (!cancelled) { setModelBases(data); setModelsLoading(false); } })
            .catch(() => { if (!cancelled) setModelsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const inputStyle = {
        width: '100%', padding: '10px 12px', fontSize: 13,
        background: 'var(--input-bg)', color: 'var(--text)',
        border: '1px solid var(--card-border)', borderRadius: 8,
        outline: 'none', transition: 'all 0.15s ease',
        fontFamily: 'inherit', boxSizing: 'border-box',
    };

    // Cost estimation based on selected model
    const getCostEstimate = () => {
        if (!form.summarizer_model) return { min: 0.14, max: 3.27 };
        const profile = MODEL_PROFILES[form.summarizer_model];
        if (!profile) return { min: 0.14, max: 3.27 };
        const cost = profile.bench.cost_run_usd;
        return { min: Math.max(0.05, cost * 0.6).toFixed(2), max: (cost * 2.5).toFixed(2) };
    };
    const costEst = getCostEstimate();

    // Quota exceeded guard
    const quotaExceeded = quota && quota.bases_active >= quota.bases_max;
    const budgetExceeded = quota && quota.budget_spent_usd >= quota.budget_max_usd;
    const blocked = quotaExceeded || budgetExceeded;

    const canSubmit = !creating && !blocked && form.name && form.template_id;

    return (
        <div
            onClick={() => !creating && onClose()}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.70)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                animation: 'expertise-fade-in 0.18s ease',
            }}
        >
            <form
                onSubmit={onSubmit}
                onClick={e => e.stopPropagation()}
                style={{
                    width: '92%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto',
                    background: 'var(--card-bg)',
                    border: `1px solid ${ACCENT_BORDER}`, borderRadius: 18,
                    padding: '28px 30px', display: 'flex', flexDirection: 'column', gap: 22,
                    boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 48px ${ACCENT_GLOW}`,
                }}
            >
                {/* ── Header ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                        width: 46, height: 46, borderRadius: 12, flexShrink: 0,
                        background: `linear-gradient(135deg, ${ACCENT_TINT}, rgba(59,130,246,0.08))`,
                        border: `1px solid ${ACCENT_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 0 16px ${ACCENT_GLOW}`,
                    }}>
                        <Hammer size={20} style={{ color: ACCENT }} />
                    </div>
                    <div>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                            Construire une base d'expertise
                        </h3>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '5px 0 0 0', lineHeight: 1.5 }}>
                            Analyse sémantique fine et structurante — l'intégralité de ton document est conservée,
                            découpée intelligemment et indexée pour être exploitable dans tes projets, affinée par d'autres modèles,
                            ou enrichie dans le temps. Pas un résumé : un <strong style={{ color: ACCENT }}>actif de connaissance évolutif</strong>.
                        </p>
                    </div>
                </div>

                {/* ── Quotas ── */}
                <div style={{
                    padding: '14px 16px', borderRadius: 12,
                    background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                    display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                    {quotaLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 12 }}>
                            <Loader size={13} className="expertise-spin" /> Vérification des quotas…
                        </div>
                    ) : quota ? (
                        <>
                            {/* Bases actives */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                        Bases actives
                                    </span>
                                    <span style={{
                                        fontSize: 12, fontWeight: 700,
                                        color: quotaExceeded ? '#ef4444' : 'var(--text)',
                                    }}>
                                        {quota.bases_active} / {quota.bases_max}
                                    </span>
                                </div>
                                <div style={{ height: 6, borderRadius: 3, background: 'var(--card-border)', overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', borderRadius: 3,
                                        width: `${Math.min(100, (quota.bases_active / quota.bases_max) * 100)}%`,
                                        background: quotaExceeded ? '#ef4444' : ACCENT,
                                        transition: 'width 0.4s ease',
                                    }} />
                                </div>
                            </div>
                            {/* Budget mensuel */}
                            {quota.budget_max_usd > 0 && (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                            Budget mensuel
                                        </span>
                                        <span style={{
                                            fontSize: 12, fontWeight: 700,
                                            color: budgetExceeded ? '#ef4444' : 'var(--text)',
                                        }}>
                                            ${(quota.budget_spent_usd || 0).toFixed(2)} / ${quota.budget_max_usd.toFixed(2)}
                                        </span>
                                    </div>
                                    <div style={{ height: 6, borderRadius: 3, background: 'var(--card-border)', overflow: 'hidden' }}>
                                        <div style={{
                                            height: '100%', borderRadius: 3,
                                            width: `${Math.min(100, ((quota.budget_spent_usd || 0) / quota.budget_max_usd) * 100)}%`,
                                            background: budgetExceeded ? '#ef4444' : '#3b82f6',
                                            transition: 'width 0.4s ease',
                                        }} />
                                    </div>
                                </div>
                            )}
                            {/* Blocked warning */}
                            {blocked && (
                                <div style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 8,
                                    padding: '10px 12px', borderRadius: 8,
                                    background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)',
                                    color: '#ef4444', fontSize: 12, fontWeight: 500, marginTop: 2,
                                }}>
                                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                    {quotaExceeded
                                        ? `Quota atteint — tu as ${quota.bases_max} bases actives. Archive une base existante pour en construire une nouvelle.`
                                        : `Budget mensuel dépassé ($${quota.budget_max_usd.toFixed(2)}). Contacte un administrateur pour continuer.`
                                    }
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            Quotas indisponibles — la construction reste possible.
                        </div>
                    )}
                </div>

                {/* ── Bases modèles disponibles (Canevas Oracle C3) ── */}
                {!modelsLoading && modelBases.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Award size={12} /> Cloner depuis un modèle validé
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {modelBases.map(m => {
                                const score = m.scoring_aggregate?.global_score;
                                const profile = m.model_metadata?.structure_profile;
                                const selected = cloneMode?.id === m.id;
                                return (
                                    <div
                                        key={m.id}
                                        onClick={() => {
                                            if (creating) return;
                                            if (selected) { setCloneMode(null); return; }
                                            setCloneMode(m);
                                            setCloneName(`${m.name} (clone)`);
                                        }}
                                        style={{
                                            padding: '11px 14px', borderRadius: 10,
                                            border: `1px solid ${selected ? '#f59e0b' : 'var(--card-border)'}`,
                                            background: selected ? 'rgba(245,158,11,0.06)' : 'var(--input-bg)',
                                            cursor: creating ? 'not-allowed' : 'pointer',
                                            display: 'flex', gap: 12, alignItems: 'center',
                                            transition: 'all 0.15s ease',
                                            boxShadow: selected ? '0 0 12px rgba(245,158,11,0.12)' : 'none',
                                        }}
                                    >
                                        <div style={{
                                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                                            background: selected ? 'rgba(245,158,11,0.15)' : 'var(--card-bg)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Award size={16} style={{ color: selected ? '#f59e0b' : 'var(--text-secondary)' }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: selected ? '#f59e0b' : 'var(--text)' }}>
                                                    {m.name}
                                                </span>
                                                {score && (
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                                        background: score >= 9 ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)',
                                                        color: score >= 9 ? '#10b981' : '#3b82f6',
                                                    }}>
                                                        {score.toFixed(1)}/10
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                                                {m.description?.slice(0, 80) || `${profile?.documents_count || '?'} doc(s) · ${profile?.dominant_gabarit || 'mixte'}`}
                                                {m.owner_name && <span style={{ marginLeft: 6, opacity: 0.7 }}>par {m.owner_name}</span>}
                                            </div>
                                        </div>
                                        {selected && <CheckCircle2 size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Formulaire de clone si un modèle est sélectionné */}
                        {cloneMode && (
                            <div style={{
                                padding: '14px 16px', borderRadius: 10,
                                background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.15)',
                                display: 'flex', flexDirection: 'column', gap: 10,
                            }}>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                                    Crée une base vide avec la même configuration que <strong style={{ color: '#f59e0b' }}>{cloneMode.name}</strong> (template, directives, modèle de synthèse).
                                </div>
                                <input
                                    type="text"
                                    value={cloneName}
                                    onChange={e => setCloneName(e.target.value)}
                                    placeholder="Nom de la nouvelle base"
                                    style={{
                                        width: '100%', padding: '8px 12px', fontSize: 13,
                                        background: 'var(--input-bg)', color: 'var(--text)',
                                        border: '1px solid var(--card-border)', borderRadius: 8,
                                        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                                    }}
                                    className="expertise-input"
                                />
                                <input
                                    type="text"
                                    value={cloneDesc}
                                    onChange={e => setCloneDesc(e.target.value)}
                                    placeholder="Description (optionnelle)"
                                    style={{
                                        width: '100%', padding: '8px 12px', fontSize: 13,
                                        background: 'var(--input-bg)', color: 'var(--text)',
                                        border: '1px solid var(--card-border)', borderRadius: 8,
                                        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                                    }}
                                    className="expertise-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => onCloneFromModel(cloneMode.id, cloneName, cloneDesc)}
                                    disabled={creating || !cloneName.trim()}
                                    style={{
                                        padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                                        background: cloneName.trim() && !creating
                                            ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                            : 'var(--card-border)',
                                        color: 'white', border: 'none',
                                        cursor: cloneName.trim() && !creating ? 'pointer' : 'not-allowed',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        boxShadow: cloneName.trim() && !creating ? '0 4px 14px rgba(245,158,11,0.25)' : 'none',
                                    }}
                                >
                                    {creating
                                        ? <><Loader size={14} className="expertise-spin" /> Clonage en cours…</>
                                        : <><GitBranch size={14} /> Cloner ce modèle</>
                                    }
                                </button>
                            </div>
                        )}

                        <div style={{ borderBottom: '1px solid var(--card-border)', margin: '4px 0' }} />
                    </div>
                )}

                {/* ── Templates — avec guidage utilisateur ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        Quel type de contenu vas-tu indexer ?
                    </label>
                    {templates.length === 0 ? (
                        <div style={{
                            padding: 14, fontSize: 12, color: 'var(--text-muted)',
                            background: 'var(--input-bg)', borderRadius: 8, textAlign: 'center',
                        }}>
                            Aucun template actif
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {templates.map(t => {
                                const Icon = TEMPLATE_ICONS[t.slug] || Database;
                                const selected = form.template_id === t.id;
                                const hint = TEMPLATE_HINTS[t.slug];
                                const isRecommended = t.slug === 'base-libre';
                                return (
                                    <div
                                        key={t.id}
                                        onClick={() => setForm({ ...form, template_id: t.id })}
                                        style={{
                                            padding: '12px 14px',
                                            border: `1px solid ${selected ? ACCENT : 'var(--card-border)'}`,
                                            background: selected ? ACCENT_TINT : 'var(--input-bg)',
                                            borderRadius: 10, cursor: 'pointer',
                                            display: 'flex', gap: 12, alignItems: 'flex-start',
                                            transition: 'all 0.15s ease',
                                            boxShadow: selected ? `0 0 12px ${ACCENT_GLOW}` : 'none',
                                            position: 'relative',
                                        }}
                                    >
                                        <div style={{
                                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                            background: selected ? `${ACCENT}22` : 'var(--card-bg)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Icon size={15} style={{ color: selected ? ACCENT : 'var(--text-secondary)' }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: selected ? ACCENT : 'var(--text)' }}>
                                                    {t.label || t.name}
                                                </span>
                                                {isRecommended && (
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                                                        background: 'rgba(34, 197, 94, 0.12)', color: '#16a34a',
                                                        border: '1px solid rgba(34, 197, 94, 0.25)',
                                                        textTransform: 'uppercase', letterSpacing: 0.3,
                                                    }}>
                                                        En cas de doute
                                                    </span>
                                                )}
                                                {hint?.pages && (
                                                    <span style={{
                                                        fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                                        background: 'rgba(139, 92, 246, 0.08)', color: 'var(--text-muted)',
                                                        border: '1px solid var(--card-border)',
                                                    }}>
                                                        {hint.pages}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.55 }}>
                                                {hint?.description || t.description}
                                            </div>
                                            {hint?.examples && (
                                                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 4, opacity: 0.7, fontStyle: 'italic' }}>
                                                    Ex : {hint.examples}
                                                </div>
                                            )}
                                        </div>
                                        {selected && (
                                            <CheckCircle2 size={16} style={{ color: ACCENT, flexShrink: 0, marginTop: 2 }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Nom ── */}
                <FormField label="Nom de la source">
                    <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        placeholder="Ex : Bibliothèque de référence ITRIIA"
                        required
                        style={inputStyle}
                        className="expertise-input"
                    />
                </FormField>

                {/* ── Slug ── */}
                <FormField label="Slug (optionnel, généré automatiquement)">
                    <input
                        type="text"
                        value={form.slug}
                        onChange={e => setForm({ ...form, slug: e.target.value })}
                        placeholder="mon-slug-personnalise"
                        style={inputStyle}
                        className="expertise-input"
                    />
                </FormField>

                {/* ── Description ── */}
                <FormField label="Description">
                    <textarea
                        value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        rows={3}
                        placeholder="À quoi sert cette base ?"
                        style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
                        className="expertise-input"
                    />
                </FormField>

                {/* ── Catégorie ── */}
                <FormField label="Catégorie (facultatif)">
                    <input
                        list="create-base-cats"
                        value={form.category || ''}
                        onChange={e => setForm({ ...form, category: e.target.value })}
                        placeholder="ex. Doctrine, Pédagogie, Production…"
                        style={inputStyle}
                        className="expertise-input"
                    />
                    <datalist id="create-base-cats">
                        {[...new Set([...SUGGESTED_CATEGORIES, ...allCategories])].map(c => <option key={c} value={c} />)}
                    </datalist>
                </FormField>

                {/* ── Modèle de synthèse ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        Modèle de synthèse
                    </label>
                    <SummarizerModelPicker
                        value={form.summarizer_model || ''}
                        onChange={val => setForm({ ...form, summarizer_model: val })}
                        disabled={creating}
                        templateDefault={null}
                    />
                    {/* Cost estimation */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 7,
                        padding: '9px 12px', borderRadius: 8,
                        background: 'rgba(139,92,246,0.07)', border: `1px solid ${ACCENT_BORDER}`,
                        fontSize: 12, color: 'var(--text-secondary)',
                    }}>
                        <Info size={13} style={{ color: ACCENT, flexShrink: 0, marginTop: 1 }} />
                        <div>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                Coût estimé : ~${costEst.min}–${costEst.max}
                            </span>
                            {' '}selon la taille du document · Construction 15–60 min
                            <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                                Chaque document uploadé sera découpé, indexé sémantiquement et synthétisé par ce modèle
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Directives de synthèse (optionnel, Canevas Oracle C2) ── */}
                <CanevasPickerInline form={form} setForm={setForm} disabled={creating} />

                {/* ── Actions ── */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 2 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={creating}
                        style={{
                            padding: '10px 18px', background: 'transparent',
                            border: '1px solid var(--card-border)', borderRadius: 9,
                            color: 'var(--text)', cursor: creating ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 500,
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        style={{
                            padding: '10px 20px',
                            background: canSubmit
                                ? `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`
                                : 'var(--card-border)',
                            color: 'white', border: 'none', borderRadius: 9,
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            fontSize: 13, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 7,
                            boxShadow: canSubmit ? `0 4px 14px ${ACCENT_GLOW}` : 'none',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {creating
                            ? <><Loader size={14} className="expertise-spin" /> Construction en cours…</>
                            : <><Hammer size={14} /> Lancer la construction</>
                        }
                    </button>
                </div>
            </form>
        </div>
    );
}

function CanevasPickerInline({ form, setForm, disabled }) {
    const [canevasList, setCanevasList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/canevas`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(data => { if (!cancelled) { setCanevasList(data); setLoading(false); } })
            .catch(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) return null;
    if (canevasList.length === 0) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: 0.3,
                }}
            >
                <ChevronDown size={12} style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                Directives de synthèse
                <span style={{ fontSize: 10, fontWeight: 400, textTransform: 'none', color: 'var(--text-muted)', letterSpacing: 0 }}>
                    (optionnel)
                </span>
                {form.canevas_prompt && (
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: 'rgba(217, 119, 6, 0.1)', color: '#d97706' }}>
                        Actif
                    </span>
                )}
            </div>

            {expanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4, marginBottom: 2 }}>
                        Oriente le modèle vers les aspects importants de tes documents. Tu pourras modifier ces directives plus tard dans les paramètres de la base.
                    </div>

                    {/* Canevas cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {canevasList.map(c => {
                            const CIcon = CANEVAS_ICONS[c.icon] || FileText;
                            const selected = form.canevas_prompt === c.prompt_content;
                            return (
                                <div key={c.id}
                                    onClick={() => {
                                        if (disabled) return;
                                        setForm({ ...form, canevas_prompt: selected ? '' : c.prompt_content });
                                    }}
                                    style={{
                                        padding: '9px 11px', borderRadius: 8,
                                        border: `1px solid ${selected ? '#d97706' : 'var(--card-border)'}`,
                                        background: selected ? 'rgba(217, 119, 6, 0.06)' : 'var(--input-bg)',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        display: 'flex', gap: 9, alignItems: 'center',
                                        transition: 'all 0.12s ease',
                                        opacity: disabled ? 0.5 : 1,
                                    }}
                                >
                                    <CIcon size={13} style={{ color: selected ? '#d97706' : 'var(--text-muted)', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: selected ? '#d97706' : 'var(--text)' }}>
                                            {c.name}
                                        </span>
                                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                                            {c.description?.slice(0, 60)}{c.description?.length > 60 ? '…' : ''}
                                        </span>
                                    </div>
                                    {selected && <CheckCircle2 size={14} style={{ color: '#d97706', flexShrink: 0 }} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// BaseTeamSection — Gestion des membres de la base
// ─────────────────────────────────────────────────────────────
function BaseTeamSection({ baseId, isOwner }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inviteRole, setInviteRole] = useState('reader');
    const [saving, setSaving] = useState(false);

    const ROLE_LABELS = { owner: 'Propriétaire', editor: 'Éditeur', reader: 'Lecteur' };
    const ROLE_COLORS = {
        owner: { bg: 'rgba(245,158,11,0.10)', color: '#f59e0b', border: 'rgba(245,158,11,0.30)' },
        editor: { bg: 'rgba(59,130,246,0.10)', color: '#3b82f6', border: 'rgba(59,130,246,0.30)' },
        reader: { bg: 'rgba(107,114,128,0.10)', color: '#6b7280', border: 'rgba(107,114,128,0.30)' },
    };

    const loadTeam = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/${baseId}/team`, { headers: authHeaders() });
            if (res.ok) setMembers(await res.json());
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [baseId]);

    useEffect(() => { loadTeam(); }, [loadTeam]);

    // Recherche utilisateurs (debounce)
    useEffect(() => {
        if (searchQuery.length < 2) { setSearchResults([]); return; }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await fetch(`${API_BASE}/users/search?q=${encodeURIComponent(searchQuery)}`, { headers: authHeaders() });
                if (res.ok) {
                    const users = await res.json();
                    // Filtrer les membres déjà présents
                    const memberIds = new Set(members.map(m => m.user_id));
                    setSearchResults(users.filter(u => !memberIds.has(u.id)));
                }
            } catch { /* silent */ }
            finally { setSearching(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, members]);

    const handleInvite = async (userId) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/${baseId}/team`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId, role: inviteRole }),
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Erreur'); }
            const data = await res.json();
            setMembers(data.team);
            setSearchQuery('');
            setSearchResults([]);
        } catch (err) { toast.error(err.message); }
        finally { setSaving(false); }
    };

    const handleChangeRole = async (userId, newRole) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/${baseId}/team/${userId}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: newRole }),
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Erreur'); }
            const data = await res.json();
            setMembers(data.team);
        } catch (err) { toast.error(err.message); }
        finally { setSaving(false); }
    };

    const handleRemove = async (userId, userName) => {
        const confirmed = await confirmModal(
            `Retirer ${userName || userId} de cette base ?`,
            { confirmText: 'Retirer', confirmStyle: 'danger' }
        );
        if (!confirmed) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/${baseId}/team/${userId}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Erreur'); }
            const data = await res.json();
            setMembers(data.team);
        } catch (err) { toast.error(err.message); }
        finally { setSaving(false); }
    };

    if (loading) return null;

    return (
        <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: 14, overflow: 'hidden',
        }}>
            {/* Header cliquable */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', cursor: 'pointer',
                    transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = ACCENT_TINT}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Users size={16} style={{ color: ACCENT }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        Équipe
                    </span>
                    <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: ACCENT_TINT, color: ACCENT,
                    }}>
                        {members.length}
                    </span>
                </div>
                <ChevronDown size={14} style={{
                    color: 'var(--text-muted)',
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s',
                }} />
            </div>

            {expanded && (
                <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Liste des membres */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {members.map(m => {
                            const rc = ROLE_COLORS[m.role] || ROLE_COLORS.reader;
                            return (
                                <div key={m.user_id} style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 12px', borderRadius: 10,
                                    background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                                }}>
                                    {/* Avatar initiales */}
                                    <div style={{
                                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                                        background: rc.bg, border: `1px solid ${rc.border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 11, fontWeight: 700, color: rc.color,
                                    }}>
                                        {(m.full_name || m.username || '?').substring(0, 2).toUpperCase()}
                                    </div>

                                    {/* Infos */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.full_name || m.username}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            {m.email || m.username}
                                        </div>
                                    </div>

                                    {/* Sélecteur de rôle (owner only) */}
                                    {isOwner ? (
                                        <select
                                            value={m.role}
                                            onChange={e => handleChangeRole(m.user_id, e.target.value)}
                                            disabled={saving}
                                            style={{
                                                padding: '4px 8px', fontSize: 11, fontWeight: 600,
                                                background: rc.bg, color: rc.color,
                                                border: `1px solid ${rc.border}`, borderRadius: 6,
                                                cursor: saving ? 'not-allowed' : 'pointer',
                                                outline: 'none', fontFamily: 'inherit',
                                            }}
                                        >
                                            <option value="owner">Propriétaire</option>
                                            <option value="editor">Éditeur</option>
                                            <option value="reader">Lecteur</option>
                                        </select>
                                    ) : (
                                        <span style={{
                                            fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                                            background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`,
                                        }}>
                                            {ROLE_LABELS[m.role]}
                                        </span>
                                    )}

                                    {/* Bouton supprimer (owner only, pas soi-même) */}
                                    {isOwner && (
                                        <button
                                            onClick={() => handleRemove(m.user_id, m.full_name || m.username)}
                                            disabled={saving}
                                            style={{
                                                width: 26, height: 26, borderRadius: 6, border: '1px solid var(--card-border)',
                                                background: 'transparent', cursor: saving ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'var(--text-muted)', transition: 'all 0.15s', padding: 0,
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.10)'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--card-border)'; }}
                                            title="Retirer de l'équipe"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Invitation — recherche et ajout (owner only) */}
                    {isOwner && (
                        <div style={{
                            padding: '12px 14px', borderRadius: 10,
                            background: ACCENT_TINT, border: `1px solid ${ACCENT_BORDER}`,
                            display: 'flex', flexDirection: 'column', gap: 8,
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: ACCENT, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                Inviter un membre
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher par nom, email…"
                                    style={{
                                        flex: 1, padding: '8px 12px', fontSize: 13,
                                        background: 'var(--input-bg)', color: 'var(--text)',
                                        border: '1px solid var(--card-border)', borderRadius: 8,
                                        outline: 'none', fontFamily: 'inherit',
                                    }}
                                    className="expertise-input"
                                />
                                <select
                                    value={inviteRole}
                                    onChange={e => setInviteRole(e.target.value)}
                                    style={{
                                        padding: '8px 10px', fontSize: 12, fontWeight: 600,
                                        background: 'var(--input-bg)', color: 'var(--text)',
                                        border: '1px solid var(--card-border)', borderRadius: 8,
                                        outline: 'none', fontFamily: 'inherit', cursor: 'pointer',
                                    }}
                                >
                                    <option value="reader">Lecteur</option>
                                    <option value="editor">Éditeur</option>
                                    <option value="owner">Propriétaire</option>
                                </select>
                            </div>

                            {/* Résultats de recherche */}
                            {searching && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                                    <Loader size={12} className="expertise-spin" /> Recherche…
                                </div>
                            )}
                            {searchResults.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {searchResults.map(u => (
                                        <div
                                            key={u.id}
                                            onClick={() => !saving && handleInvite(u.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '7px 10px', borderRadius: 8,
                                                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                                                cursor: saving ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.12s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.background = ACCENT_TINT; }}
                                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.background = 'var(--card-bg)'; }}
                                        >
                                            <div style={{
                                                width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                                                background: ACCENT_TINT, border: `1px solid ${ACCENT_BORDER}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 10, fontWeight: 700, color: ACCENT,
                                            }}>
                                                {(u.full_name || u.username || '?').substring(0, 2).toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                                                    {u.full_name || u.username}
                                                </span>
                                                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                                                    {u.email || u.username}
                                                </span>
                                            </div>
                                            <Plus size={14} style={{ color: ACCENT, flexShrink: 0 }} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '6px 0' }}>
                                    Aucun utilisateur trouvé
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <label style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: 0.3,
            }}>
                {label}
            </label>
            {children}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// CSS animations + hover effects
// ═══════════════════════════════════════════════════════════════
const INLINE_CSS = `
@keyframes expertise-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
@keyframes expertise-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}
@keyframes expertise-progress-slide {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
    100% { transform: translateX(400%); }
}
.expertise-spin {
    animation: expertise-spin 1s linear infinite;
}
.expertise-progress-bar {
    animation: expertise-progress-slide 1.6s ease-in-out infinite;
}
.expertise-base-card:hover {
    border-color: ${ACCENT_BORDER} !important;
    box-shadow: 0 4px 20px ${ACCENT_GLOW};
    transform: translateY(-2px);
}
.expertise-doc-card:hover {
    border-color: ${ACCENT_BORDER} !important;
    background: ${ACCENT_TINT} !important;
}
.expertise-doc-card:hover .expertise-doc-delete {
    color: #ef4444 !important;
    border-color: rgba(239,68,68,0.3) !important;
}
.expertise-doc-delete:hover {
    background: rgba(239,68,68,0.12) !important;
}
.expertise-input:focus {
    border-color: ${ACCENT} !important;
    box-shadow: 0 0 0 3px ${ACCENT_GLOW};
}
`;

// ─────────────────────────────────────────────────────────────
// MetricCard — Carte métrique compacte pour le panel admin
// ─────────────────────────────────────────────────────────────
function MetricCard({ icon, label, value, sub }) {
    return (
        <div style={{
            padding: '12px 14px',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'var(--text-muted)' }}>{icon}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>
                {value}
            </div>
            {sub && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// ArchivedBaseRow — Ligne base archivée avec actions
// ─────────────────────────────────────────────────────────────
function ArchivedBaseRow({ base, isAdmin, onUnarchive, onPurge, unarchiving, unarchiveProgress }) {
    const archiveMeta = base.config_overrides?.archive_metadata;
    const isRestoring = unarchiving === base.id;

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
            gap: 12,
            opacity: isRestoring ? 0.7 : 1,
            transition: 'opacity 0.2s',
        }}>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {base.name}
                    </span>
                    {base.is_model && (
                        <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6,
                            background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                            border: '1px solid rgba(245,158,11,0.25)',
                        }}>Modèle</span>
                    )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>{parseInt(base.document_count) || 0} doc(s)</span>
                    <span>·</span>
                    <span>Archivée {formatDate(base.archived_at)}</span>
                    {archiveMeta?.chunks_freed > 0 && (
                        <>
                            <span>·</span>
                            <span>{archiveMeta.chunks_freed} chunks libérés</span>
                        </>
                    )}
                    {base.owner_name && (
                        <>
                            <span>·</span>
                            <span>{base.owner_name}</span>
                        </>
                    )}
                </div>
                {isRestoring && unarchiveProgress && (
                    <div style={{
                        fontSize: 11, color: ACCENT, marginTop: 4,
                        display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                        <Loader size={12} className="expertise-spin" />
                        {unarchiveProgress.label}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                    onClick={() => onUnarchive(base)}
                    disabled={isRestoring}
                    title="Restaurer cette base"
                    style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '6px 12px', fontSize: 11, fontWeight: 600,
                        background: isRestoring ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.1)',
                        color: ACCENT, border: `1px solid ${ACCENT_BORDER}`,
                        borderRadius: 7, cursor: isRestoring ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!isRestoring) { e.currentTarget.style.background = 'rgba(139,92,246,0.18)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = isRestoring ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.1)'; }}
                >
                    <ArchiveRestore size={13} />
                    Restaurer
                </button>
                {isAdmin && (
                    <button
                        onClick={() => onPurge(base)}
                        disabled={isRestoring}
                        title="Supprimer définitivement"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '6px 10px', fontSize: 11, fontWeight: 600,
                            background: 'rgba(239,68,68,0.08)',
                            color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)',
                            borderRadius: 7, cursor: isRestoring ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (!isRestoring) e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                    >
                        <Trash2 size={13} />
                        Purger
                    </button>
                )}
            </div>
        </div>
    );
}

export default ExpertiseBasesPanel;
